import { ChangeDetectionStrategy, Component, OnInit, DestroyRef, ChangeDetectorRef, Injector } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { GridLayoutService } from '../services/grid-layout.service';
import { InspectorConfigService } from '../services/inspector-config.service';
import { TabControlService } from '../services/tab-control.service';
import { IconManagerService, IconOption } from '../services/icon-manager.service';
import { DataBindingService } from '../services/data-binding.service';
import { BuilderNode, BuilderComponentType, GridPosition, TabInfo, ContextFieldConfig, SearchFieldDependency, SearchFieldMapping } from '../interfaces/builder-screen.models';
import { DropdownOptionsModalComponent } from './dropdown-options-modal.component';
import { ContextFieldsModalComponent } from './context-fields-modal.component';
import { SearchDependencyModalComponent, DependencyFieldOption } from './search-dependency-modal.component';
import { FieldMappingModalComponent, FieldMappingTarget } from './field-mapping-modal.component';
import { NumberFormatModalComponent, NumberFormatModalConfig } from './number-format-modal.component';
import { InspectorProperty, ComponentPreset, PropertyGroup } from '../interfaces/inspector-config.models';
import { distinctUntilChanged, combineLatest } from 'rxjs';
import { Subscription } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { MatDialog } from '@angular/material/dialog';
import { FlowConfigModalComponent, FlowConfigData } from './flow-config-modal/flow-config-modal.component';

@Component({
  selector: 'app-builder-inspector-beta',
  templateUrl: './inspector.component.html',
  styleUrls: ['./inspector.component.scss', './inspector-flow-config.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InspectorBetaComponent implements OnInit {
  public form: FormGroup = this.createBaseForm();
  private currentSelectedId: string | null = null;
  private currentType: BuilderComponentType | null = null;
  private formSub?: Subscription;
  
  // ✅ Timestamp até quando devemos pular applySelectedNode (previne reset após salvar modal)
  private _skipUntilTimestamp = 0;
  
  // Propriedades da Grade V3
  public currentGridPosition: GridPosition | null = null;

  // Propriedades de UI Aprimoradas V5
  public selectedComponentType: BuilderComponentType | null = null;
  public essentialProperties: InspectorProperty[] = [];
  public advancedProperties: InspectorProperty[] = [];
  public propertyGroups: PropertyGroup[] = [];
  public componentPresets: ComponentPreset[] = [];
  public showPresetSelector = false;
  
  // Seletor de cores simplificado sem presets
  
  // Propriedades para controle de abas do Switch
  public availableTabs: TabInfo[] = [];
  public showTabControl = false;
  private isInitializingTabControl = false;

  // Propriedades para ícones de aba
  public availableTabIcons: IconOption[] = [];
  public iconCategories: { [key: string]: string } = {};
  
  //  NOVO: Data Binding
  public dataPathPreview: string = '';

  // Busca no Inspector
  public inspectorSearchTerm = '';

  // Importação de campos para container selecionado
  public showImportFieldsModal = false;
  public importGroups: Array<{
    panelId: string;
    panelName: string;
    tabLabel: string;
    expanded: boolean;
    items: Array<{
      id: string;
      label: string;
      type: string;
      selected: boolean;
      isRow: boolean;
      childCount?: number;
      children?: Array<{ id: string; label: string; type: string }>;
    }>;
  }> = [];
  public importSearchText = '';

  // Barra de Contexto (Context Strip) — configuração via modal

  constructor(
    private readonly fb: FormBuilder,
    private readonly builderService: BuilderScreenBetaService,
    private readonly gridLayoutService: GridLayoutService,
    private readonly inspectorConfig: InspectorConfigService,
    private readonly tabControlService: TabControlService,
    private readonly iconManagerService: IconManagerService,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly message: NzMessageService,
    private readonly translate: TranslateService,
    public readonly uas: UserAuthenticationServices,
    private readonly dialog: MatDialog,
    private readonly injector: Injector,
    private readonly modal: NzModalService
  ) {
    this.builderService.selectedNode$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          // Compara apenas o ID e tipo - se forem iguais, ignora emissão
          return prev?.id === curr?.id && prev?.type === curr?.type;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((node) => {
        // Corrigido inspetor mostrando valores obsoletos ao selecionar nós diferentes     
        this.applySelectedNode(node);
      });

    // Rastreamento de posição da Grade V3
    combineLatest([
      this.builderService.nodes$,
      this.builderService.selectedId$
    ]).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(([nodes, selectedId]) => {
      if (selectedId) {
        const positions = this.gridLayoutService.calculateGridPosition([...nodes]);
        this.currentGridPosition = positions.get(selectedId) || null;
        this.cdr.markForCheck();
      }
    });

    this.bindFormChanges();

    // Garante que qualquer subscrição dinâmica seja limpa
    this.destroyRef.onDestroy(() => {
      this.formSub?.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.loadAvailableTabIcons();
  }

  private loadAvailableTabIcons(): void {
    this.availableTabIcons = this.iconManagerService.getAvailableIcons();
    this.iconCategories = this.iconManagerService.getCategories();
  }

  private bindFormChanges(): void {
    // Re-vincula à instância atual do formulário para que atualizações se propaguem após recriarmos o formulário em mudanças de seleção
    // Re-subscreve com segurança desfazendo subscrição anterior
    this.formSub?.unsubscribe();
    this.formSub = this.form.valueChanges
      .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(values => {
        if (!this.currentSelectedId) return;
        const base: any = {
          label: values['label'],
          placeholder: values['placeholder'],
          rowSpan: values['rowSpan'],
          colSpan: values['colSpan'],
          isDisabled: values['isDisabled'] ?? false, // ✅ Propagar isDisabled para todos os tipos
          // Propriedades de mapeamento de banco de dados
          entityName: values['entityName'],
          fieldName: values['fieldName'],
          dataPath: values['dataPath'] //  NOVO
        };
        if (this.currentType === 'text') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            required: values['required'],
            minLength: values['minLength'],
            maxLength: values['maxLength'],
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor'],
            defaultValue: values['defaultValueText'] // NOVO: Valor padrão
          } as any);
          return;
        }
        if (this.currentType === 'textarea') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            required: values['required'],
            minRows: values['minRows'],
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor'],
            defaultValue: values['defaultValueText'] // NOVO: Valor padrão
          } as any);
          return;
        }
        if (this.currentType === 'button') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            text: values['text'],
            buttonType: values['buttonType'],
            size: values['size'],
            disabled: values['disabled'],
            loading: values['loading'],
            icon: values['icon'],
            actionType: values['actionType'],
            flowConfig: values['flowConfig'], // ✅ Incluir flowConfig
            flowDataMode: values['flowDataMode'] || 'full', // ✅ Incluir flowDataMode
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor']
          } as any);
          return;
        }
        if (this.currentType === 'image') {
          console.log('🖼️ [Inspector] Salvando props da imagem:', values);
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            imageUrl: values['imageUrl'],
            alt: values['alt'],
            width: values['width'],
            height: values['height'],
            borderRadius: values['borderRadius'],
            enableLightbox: values['enableLightbox']
          } as any);
          return;
        }
        if (this.currentType === 'number') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            required: values['required'],
            min: values['min'],
            max: values['max'],
            step: values['step'],
            precision: values['precision'],
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor'],
            defaultValue: values['defaultValueNumber'] // NOVO: Valor padrão
          } as any);
          return;
        }
        if (this.currentType === 'switch') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            defaultValue: values['defaultValue'],
            checkedChildren: values['checkedChildren'],
            unCheckedChildren: values['unCheckedChildren'],
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor']
          } as any);
          return;
        }
        if (this.currentType === 'simpletable' || this.currentType === 'table') {
          const columns = (values['columns'] as any[])?.map(col => ({
            key: col.key || '',
            label: col.label || '',
            type: col.type || 'text',
            sortable: col.sortable || false,
            filterable: col.filterable || false
          })) || [];
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            sorting: values['sorting'],
            filtering: values['filtering'],
            pagination: values['pagination'],
            columns,
            headerBackgroundColor: values['headerBackgroundColor'],
            headerTextColor: values['headerTextColor'],
            rowBackgroundColor: values['rowBackgroundColor'],
            rowTextColor: values['rowTextColor'],
            alternateRowColor: values['alternateRowColor'],
            borderColor: values['borderColor'],
            hoverColor: values['hoverColor']
          } as any);
          return;
        }
        if (this.currentType === 'datagrid') {
          const columns = (values['columns'] as any[])?.map(col => {
            const mapped: any = {
              field: col.field || '',
              header: col.header || '',
              type: col.type || 'text',
              dataPath: col.dataPath || '',
              width: col.width || '',
              sortable: col.sortable || false,
              filterable: col.filterable || false,
              editable: col.editable || false
            };
            if (col.searchProperties) mapped.searchProperties = col.searchProperties;
            if (col.showKey) mapped.showKey = col.showKey;
            if (col.fieldMappings) mapped.fieldMappings = col.fieldMappings;
            if (col.options) mapped.options = col.options;
            if (col.multiple) mapped.multiple = col.multiple;
            if (col.showSearch) mapped.showSearch = col.showSearch;
            return mapped;
          }) || [];
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            label: values['label'], //  NOVO: Label customizável da grade
            dataPath: values['dataPath'],
            previewMode: values['previewMode'], // ✅ NOVO
            enablePagination: values['enablePagination'],
            pageSize: values['pageSize'],
            maxVisibleRows: values['maxVisibleRows'],
            enableSorting: values['enableSorting'],
            enableFilters: values['enableFilters'],
            enableCRUD: values['enableCRUD'],
            selectionMode: values['selectionMode'],
            //  NOVO: Props de Master-Detail
            enableMasterDetail: values['enableMasterDetail'],
            masterDetailFormPanel: values['masterDetailFormPanel'],
            masterDetailFormPanels: values['masterDetailFormPanels'],
            masterDetailAutoGenerate: values['masterDetailAutoGenerate'],
            autoAddFirstRow: values['autoAddFirstRow'],
            autoAddOrder: values['autoAddOrder'],
            viewMode: values['viewMode'] ?? 'inline',
            drawerWidth: values['drawerWidth'] ?? 40,
            columns
          } as any);
          
          //  NOVO: Auto-gerar campos se checkbox estiver marcado
          // Suporta tanto array quanto single panel
          const panelIds = values['masterDetailFormPanels']?.length > 0 
            ? values['masterDetailFormPanels'] 
            : (values['masterDetailFormPanel'] ? [values['masterDetailFormPanel']] : []);
          
          if (values['masterDetailAutoGenerate'] && panelIds.length > 0 && columns.length > 0) {
            panelIds.forEach((panelId: string) => {
              this.autoGenerateFormFields(panelId, columns);
            });
          }
          
          return;
        }
        // Tratamento de componentes Webhook V5
        if (this.builderService.isWebhookComponent(this.currentType!)) {
          const webhookProps = this.getWebhookSpecificProps(values);
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            ...webhookProps
          } as any);
          return;
        }
        if (this.currentType === 'tabs') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor'],
            activeTabColor: values['activeTabColor'],
            activeTabTextColor: values['activeTabTextColor'],
            borderColor: values['borderColor'],
            contentBackgroundColor: values['contentBackgroundColor'],
            contentTextColor: values['contentTextColor'],
            hoverTabColor: values['hoverTabColor'],
            tabsContainerBackgroundColor: values['tabsContainerBackgroundColor'],
            tabsContainerBorderColor: values['tabsContainerBorderColor'],
            tabsContainerBorderRadius: values['tabsContainerBorderRadius'],
            tabsContainerPadding: values['tabsContainerPadding'],
            wrapperBackgroundColor: values['wrapperBackgroundColor'],
            wrapperBorderColor: values['wrapperBorderColor'],
            wrapperBorderRadius: values['wrapperBorderRadius'],
            wrapperPadding: values['wrapperPadding']
          } as any);
          return;
        }
        if (this.currentType === 'panel') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            name: values['name'],
            order: values['order'],
            active: values['active'],
            disabled: values['disabled'],
            collapsible: values['collapsible'],
            collapsed: values['collapsed'],
            backgroundColor: values['backgroundColor'],
            textColor: values['textColor'],
            panelBorderColor: values['panelBorderColor'],
            panelHeaderBackgroundColor: values['panelHeaderBackgroundColor'],
            panelHeaderTextColor: values['panelHeaderTextColor'],
            panelContentBackgroundColor: values['panelContentBackgroundColor']
          } as any);
          return;
        }
        if (this.currentType === 'spreadsheet') {
          this.builderService.updateNodeProps(this.currentSelectedId, {
            ...base,
            showToolbar: values['showToolbar'],
            showFormulaBar: values['showFormulaBar'],
            showContextMenu: values['showContextMenu'],
            readonly: values['readonly'],
            backgroundColor: values['backgroundColor'],
            borderColor: values['borderColor'],
            headerBackgroundColor: values['headerBackgroundColor'],
            headerTextColor: values['headerTextColor'],
            gridColor: values['gridColor'],
            defaultRowHeight: values['defaultRowHeight'],
            defaultColumnWidth: values['defaultColumnWidth'],
            maxRows: values['maxRows'],
            maxColumns: values['maxColumns'],
            allowImportExport: values['allowImportExport'],
            enableFormulas: values['enableFormulas'],
            enableCharts: values['enableCharts']
          } as any);
          return;
        }
        // Fallback padrão
        this.builderService.updateNodeProps(this.currentSelectedId, base as any);
      });
  }

  private createBaseForm(): FormGroup {
    // Corrigido: confirma atualizações imediatamente na mudança para que a troca de seleção não perca edições
    return this.fb.group({
      label: this.fb.control<string>({ value: '', disabled: true }, [Validators.maxLength(256)]),
      placeholder: this.fb.control<string>({ value: '', disabled: true }, [Validators.maxLength(256)]),
      rowSpan: this.fb.control<number>({ value: 1, disabled: true }, [Validators.min(1)]),
      colSpan: this.fb.control<number>({ value: 24, disabled: true }, [Validators.min(1), Validators.max(24)]),
      required: this.fb.control<boolean>({ value: false, disabled: true }),
      isDisabled: this.fb.control<boolean>({ value: false, disabled: true }),
      minLength: this.fb.control<number | null>({ value: null, disabled: true }),
      maxLength: this.fb.control<number | null>({ value: null, disabled: true }),
      minRows: this.fb.control<number | null>({ value: null, disabled: true }, [Validators.min(1)]),
      // Controles de componente numérico
      min: this.fb.control<number | null>({ value: null, disabled: true }, [this.minMaxValidator.bind(this)]),
      max: this.fb.control<number | null>({ value: null, disabled: true }, [this.minMaxValidator.bind(this)]),
      step: this.fb.control<number | null>({ value: null, disabled: true }, [Validators.min(0.01)]),
      precision: this.fb.control<number | null>({ value: null, disabled: true }, [Validators.min(0)]),
      // Controles de formatação de Number (NG-ZORRO)
      decimalPlaces: this.fb.control<number | null>({ value: 2, disabled: true }, [Validators.min(0), Validators.max(10)]),
      formatThousands: this.fb.control<boolean>({ value: false, disabled: true }),
      thousandSeparator: this.fb.control<string>({ value: '.', disabled: true }),
      decimalSeparator: this.fb.control<string>({ value: ',', disabled: true }),
      addonBefore: this.fb.control<string>({ value: '', disabled: true }),
      addonAfter: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de valor padrão (defaultValue) - tipos diferentes para cada componente
      defaultValue: this.fb.control<boolean>({ value: false, disabled: true }), // Para switch
      defaultValueText: this.fb.control<string>({ value: '', disabled: true }), // Para text, textarea, date
      defaultValueNumber: this.fb.control<number | null>({ value: null, disabled: true }), // Para number
      // Controles de componente switch
      checkedChildren: this.fb.control<string>({ value: '', disabled: true }, [Validators.maxLength(50)]),
      unCheckedChildren: this.fb.control<string>({ value: '', disabled: true }, [Validators.maxLength(50)]),
      // Controles de controle de abas do switch
      tabControl: this.fb.group({
        enabled: this.fb.control<boolean>({ value: false, disabled: false }),
        onTrueAction: this.fb.control<'show' | 'hide'>({ value: 'show', disabled: true }, [Validators.required]),
        onFalseAction: this.fb.control<'show' | 'hide'>({ value: 'hide', disabled: true }, [Validators.required]),
        targetTabs: this.fb.control<string[]>({ value: [], disabled: true }, [Validators.required]),
        mode: this.fb.control<'include' | 'exclude'>({ value: 'include', disabled: true }, [Validators.required])
      }),
      // Controles de componente de tabela
      sorting: this.fb.control<boolean>({ value: false, disabled: true }),
      filtering: this.fb.control<boolean>({ value: false, disabled: true }),
      pagination: this.fb.control<boolean>({ value: true, disabled: true }),
      columns: this.fb.array([], [this.tableColumnsValidator.bind(this)]),
      // Controles de componente Webhook V5
      dateFormat: this.fb.control<string>({ value: 'DD/MM/YYYY', disabled: true }),
      showTime: this.fb.control<boolean>({ value: false, disabled: true }),
      multiple: this.fb.control<boolean>({ value: false, disabled: true }),
      showSearch: this.fb.control<boolean>({ value: false, disabled: true }),
      //  NOVO: FormArray para opções do Dropdown
      dropdownOptions: this.fb.array([]),
      condition: this.fb.control<string>({ value: '', disabled: true }),
      showKey: this.fb.control<boolean>({ value: false, disabled: true }),
      entityName: this.fb.control<string>({ value: '', disabled: true }),
      keyField: this.fb.control<string>({ value: '', disabled: true }),
      valueField: this.fb.control<string>({ value: '', disabled: true }),
      searchEntityName: this.fb.control<string>({ value: '', disabled: true }), //  Para componente Search
      searchKeyField: this.fb.control<string>({ value: '', disabled: true }), //  Para componente Search
      searchValueField: this.fb.control<string>({ value: '', disabled: true }), //  Para componente Search
      dashed: this.fb.control<boolean>({ value: false, disabled: true }),
      dividerType: this.fb.control<string>({ value: 'horizontal', disabled: true }),
      orientation: this.fb.control<string>({ value: 'center', disabled: true }),
      color: this.fb.control<string>({ value: '#d9d9d9', disabled: true }),
      title: this.fb.control<string>({ value: '', disabled: true }),
      collapsible: this.fb.control<boolean>({ value: false, disabled: true }),
      collapsed: this.fb.control<boolean>({ value: false, disabled: true }),
      // Controles de Panel
      name: this.fb.control<string>({ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]),
      active: this.fb.control<boolean>({ value: false, disabled: true }),
      order: this.fb.control<number>({ value: 1, disabled: true }, [Validators.min(1), Validators.max(100)]),
      //  NOVO: Controles de Row (container horizontal)
      gap: this.fb.control<string>({ value: '12px', disabled: true }),
      alignItems: this.fb.control<string>({ value: 'start', disabled: true }),
      justifyContent: this.fb.control<string>({ value: 'start', disabled: true }),
      padding: this.fb.control<string>({ value: '8px', disabled: true }),
      // Controles de botão
      text: this.fb.control<string>({ value: '', disabled: true }),
      buttonType: this.fb.control<string>({ value: 'default', disabled: true }),
      size: this.fb.control<string>({ value: 'default', disabled: true }),
      disabled: this.fb.control<boolean>({ value: false, disabled: true }),
      loading: this.fb.control<boolean>({ value: false, disabled: true }),
      icon: this.fb.control<string>({ value: '', disabled: true }),
      actionType: this.fb.control<string>({ value: 'custom', disabled: true }),
      // Controles de configuração de fluxo
      flowConfig: this.fb.group({
        idConf: this.fb.control<number | null>({ value: null, disabled: true }),
        confName: this.fb.control<string>({ value: '', disabled: true }),
        showCodeCompany: this.fb.control<boolean>({ value: false, disabled: true }),
        codeCompany: this.fb.control<number | null>({ value: null, disabled: true }),
        newMode: this.fb.control<boolean>({ value: false, disabled: true })
      }),
      flowDataMode: this.fb.control<string>({ value: 'full', disabled: true }),
      // Controles de planilha
      showToolbar: this.fb.control<boolean>({ value: true, disabled: true }),
      showFormulaBar: this.fb.control<boolean>({ value: true, disabled: true }),
      showContextMenu: this.fb.control<boolean>({ value: true, disabled: true }),
      readonly: this.fb.control<boolean>({ value: false, disabled: true }),
      defaultRowHeight: this.fb.control<number>({ value: 25, disabled: true }, [Validators.min(20), Validators.max(100)]),
      defaultColumnWidth: this.fb.control<number>({ value: 100, disabled: true }, [Validators.min(50), Validators.max(300)]),
      maxRows: this.fb.control<number>({ value: 100, disabled: true }, [Validators.min(10), Validators.max(1000)]),
      maxColumns: this.fb.control<number>({ value: 26, disabled: true }, [Validators.min(5), Validators.max(100)]),
      allowImportExport: this.fb.control<boolean>({ value: true, disabled: true }),
      enableFormulas: this.fb.control<boolean>({ value: true, disabled: true }),
      enableCharts: this.fb.control<boolean>({ value: false, disabled: true }),
      // Controles de cor da planilha
      borderColor: this.fb.control<string>({ value: '#d9d9d9', disabled: true }),
      headerBackgroundColor: this.fb.control<string>({ value: '#f5f5f5', disabled: true }),
      headerTextColor: this.fb.control<string>({ value: '#262626', disabled: true }),
      gridColor: this.fb.control<string>({ value: '#e8e8e8', disabled: true }),
      backgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      textColor: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de cor das abas
      activeTabColor: this.fb.control<string>({ value: '', disabled: true }),
      activeTabTextColor: this.fb.control<string>({ value: '', disabled: true }),
      contentBackgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      contentTextColor: this.fb.control<string>({ value: '', disabled: true }),
      hoverTabColor: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de cor do container das tabs
      tabsContainerBackgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      tabsContainerBorderColor: this.fb.control<string>({ value: '', disabled: true }),
      tabsContainerBorderRadius: this.fb.control<string>({ value: '', disabled: true }),
      tabsContainerPadding: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de cor do wrapper externo das tabs
      wrapperBackgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      wrapperBorderColor: this.fb.control<string>({ value: '', disabled: true }),
      wrapperBorderRadius: this.fb.control<string>({ value: '', disabled: true }),
      wrapperPadding: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de cor do painel
      panelBorderColor: this.fb.control<string>({ value: '', disabled: true }),
      panelHeaderBackgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      panelHeaderTextColor: this.fb.control<string>({ value: '', disabled: true }),
      panelContentBackgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de cor da tabela
      rowBackgroundColor: this.fb.control<string>({ value: '', disabled: true }),
      rowTextColor: this.fb.control<string>({ value: '', disabled: true }),
      alternateRowColor: this.fb.control<string>({ value: '', disabled: true }),
      hoverColor: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de mapeamento de banco de dados (entityName já existe para componentes webhook)
      fieldName: this.fb.control<string>({ value: '', disabled: true }),
      // Controle para ícone da aba
      tabIcon: this.fb.control<string>({ value: '', disabled: true }),
      // Controles de DataGrid
      dataPath: this.fb.control<string>({ value: '', disabled: true }),
      previewMode: this.fb.control<boolean>({ value: false, disabled: true }), // ✅ NOVO: Preview Mode
      enablePagination: this.fb.control<boolean>({ value: true, disabled: true }),
      pageSize: this.fb.control<number>({ value: 10, disabled: true }, [Validators.min(1), Validators.max(100)]),
      maxVisibleRows: this.fb.control<number>({ value: 0, disabled: true }, [Validators.min(0), Validators.max(100)]),
      enableSorting: this.fb.control<boolean>({ value: true, disabled: true }),
      enableFilters: this.fb.control<boolean>({ value: false, disabled: true }),
      enableCRUD: this.fb.control<boolean>({ value: false, disabled: true }),
      selectionMode: this.fb.control<string>({ value: 'none', disabled: true }),
      //  NOVO: Controles de Master-Detail
      enableMasterDetail: this.fb.control<boolean>({ value: false, disabled: true }),
      masterDetailFormPanel: this.fb.control<string | null>({ value: null, disabled: true }),
      masterDetailFormPanels: this.fb.control<string[]>({ value: [], disabled: true }),
      masterDetailAutoGenerate: this.fb.control<boolean>({ value: false, disabled: true }),
      // Controles de Auto-Add primeira linha
      autoAddFirstRow: this.fb.control<boolean>({ value: false, disabled: true }),
      autoAddOrder: this.fb.control<number>({ value: 0, disabled: true }, [Validators.min(0)]),
      // Controles de View Mode
      viewMode: this.fb.control<string>({ value: 'inline', disabled: true }),
      drawerWidth: this.fb.control<number>({ value: 40, disabled: true }, [Validators.min(10), Validators.max(90)]),
      //  NOVO: Controles de Image
      imageUrl: this.fb.control<string>({ value: '', disabled: true }),
      alt: this.fb.control<string>({ value: '', disabled: true }),
      width: this.fb.control<number>({ value: 300, disabled: true }, [Validators.min(0)]),
      height: this.fb.control<number>({ value: 200, disabled: true }, [Validators.min(0)]),
      borderRadius: this.fb.control<number>({ value: 0, disabled: true }, [Validators.min(0)]),
      enableLightbox: this.fb.control<boolean>({ value: true, disabled: true }),
    }, { updateOn: 'change' });
  }

  private applySelectedNode(node: BuilderNode | null): void {
    // ✅ CORREÇÃO: Pular se acabamos de salvar valores do modal (previne reset)
    const now = Date.now();
    if (this._skipUntilTimestamp && now < this._skipUntilTimestamp && node && node.id === this.currentSelectedId) {
      this._skipUntilTimestamp = 0;
      return;
    }
    if (this._skipUntilTimestamp && now >= this._skipUntilTimestamp) {
      this._skipUntilTimestamp = 0;
    }
    
    if (!node) {
      this.currentSelectedId = null;
      this.currentType = null;
      this.updateInspectorForComponent(null);
      this.form.reset({ 
        label: '', placeholder: '', rowSpan: 1, colSpan: 24, required: false, 
        minLength: null, maxLength: null, minRows: null,
        min: null, max: null, step: null, precision: null,
        defaultValue: false, checkedChildren: '', unCheckedChildren: '',
        sorting: false, filtering: false, pagination: true
      });
      this.columnsArray.clear();
      this.form.disable({ emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    this.currentSelectedId = node.id;
    this.currentType = node.type;
    // Atualiza inspetor para novo tipo de componente
    this.updateInspectorForComponent(node.type);
    // Recria controles para evitar estados desabilitados/habilitados residuais da seleção anterior
    this.form = this.createBaseForm();
    this.bindFormChanges();
    this.form.enable({ emitEvent: false });
    const base = {
      label: (node.props as any).label ?? '',
      placeholder: (node.props as any).placeholder ?? '',
      rowSpan: (node.props as any).rowSpan ?? 1,
      colSpan: (node.props as any).colSpan ?? 24,
      order: (node.props as any).order ?? 0, // ✅ NOVO: Ordem do componente
      isDisabled: (node.props as any).isDisabled ?? false, // ✅ Carregar isDisabled do node
      // Propriedades de mapeamento de banco de dados
      entityName: (node.props as any).entityName ?? '',
      fieldName: (node.props as any).fieldName ?? '',
      dataPath: (node.props as any).dataPath ?? '', //  NOVO: Caminho dos dados
    };

    // Reseta controles específicos do tipo
    this.form.get('required')?.disable({ emitEvent: false });
    this.form.get('minLength')?.disable({ emitEvent: false });
    this.form.get('maxLength')?.disable({ emitEvent: false });
    this.form.get('minRows')?.disable({ emitEvent: false });
    this.form.get('min')?.disable({ emitEvent: false });
    this.form.get('max')?.disable({ emitEvent: false });
    this.form.get('step')?.disable({ emitEvent: false });
    this.form.get('precision')?.disable({ emitEvent: false });
    this.form.get('defaultValue')?.disable({ emitEvent: false });
    this.form.get('checkedChildren')?.disable({ emitEvent: false });
    this.form.get('unCheckedChildren')?.disable({ emitEvent: false });
    this.form.get('tabControl.enabled')?.disable({ emitEvent: false });
    this.form.get('tabControl.onTrueAction')?.disable({ emitEvent: false });
    this.form.get('tabControl.onFalseAction')?.disable({ emitEvent: false });
    this.form.get('tabControl.targetTabs')?.disable({ emitEvent: false });
    this.form.get('tabControl.mode')?.disable({ emitEvent: false });
    this.form.get('sorting')?.disable({ emitEvent: false });
    this.form.get('filtering')?.disable({ emitEvent: false });
    this.form.get('pagination')?.disable({ emitEvent: false });
    this.columnsArray.disable({ emitEvent: false });
    // V5 Desabilita controles webhook
    this.form.get('dateFormat')?.disable({ emitEvent: false });
    this.form.get('showTime')?.disable({ emitEvent: false });
    this.form.get('multiple')?.disable({ emitEvent: false });
    this.form.get('showSearch')?.disable({ emitEvent: false });
    this.form.get('condition')?.disable({ emitEvent: false });
    this.form.get('showKey')?.disable({ emitEvent: false });
    this.form.get('entityName')?.disable({ emitEvent: false });
    this.form.get('keyField')?.disable({ emitEvent: false });
    this.form.get('valueField')?.disable({ emitEvent: false });
    this.form.get('dashed')?.disable({ emitEvent: false });
    this.form.get('dividerType')?.disable({ emitEvent: false });
    this.form.get('orientation')?.disable({ emitEvent: false });
    this.form.get('color')?.disable({ emitEvent: false });
    this.form.get('title')?.disable({ emitEvent: false });
    this.form.get('collapsible')?.disable({ emitEvent: false });
    this.form.get('collapsed')?.disable({ emitEvent: false });
    // Desabilita controles de botão
    this.form.get('text')?.disable({ emitEvent: false });
    this.form.get('buttonType')?.disable({ emitEvent: false });
    this.form.get('size')?.disable({ emitEvent: false });
    this.form.get('disabled')?.disable({ emitEvent: false });
    this.form.get('loading')?.disable({ emitEvent: false });
    this.form.get('icon')?.disable({ emitEvent: false });
    // Desabilita controles de planilha
    this.form.get('showToolbar')?.disable({ emitEvent: false });
    this.form.get('showFormulaBar')?.disable({ emitEvent: false });
    this.form.get('showContextMenu')?.disable({ emitEvent: false });
    this.form.get('readonly')?.disable({ emitEvent: false });
    this.form.get('defaultRowHeight')?.disable({ emitEvent: false });
    this.form.get('defaultColumnWidth')?.disable({ emitEvent: false });
    this.form.get('maxRows')?.disable({ emitEvent: false });
    this.form.get('maxColumns')?.disable({ emitEvent: false });
    this.form.get('allowImportExport')?.disable({ emitEvent: false });
    this.form.get('enableFormulas')?.disable({ emitEvent: false });
    this.form.get('enableCharts')?.disable({ emitEvent: false });
    this.form.get('backgroundColor')?.disable({ emitEvent: false });
    this.form.get('textColor')?.disable({ emitEvent: false });
    // Desabilita controles de cor das abas
    this.form.get('activeTabColor')?.disable({ emitEvent: false });
    this.form.get('activeTabTextColor')?.disable({ emitEvent: false });
    this.form.get('borderColor')?.disable({ emitEvent: false });
    this.form.get('contentBackgroundColor')?.disable({ emitEvent: false });
    this.form.get('contentTextColor')?.disable({ emitEvent: false });
    // Desabilita controles de cor da tabela
    this.form.get('headerBackgroundColor')?.disable({ emitEvent: false });
    this.form.get('headerTextColor')?.disable({ emitEvent: false });
    this.form.get('rowBackgroundColor')?.disable({ emitEvent: false });
    this.form.get('rowTextColor')?.disable({ emitEvent: false });
    this.form.get('alternateRowColor')?.disable({ emitEvent: false });
    this.form.get('hoverColor')?.disable({ emitEvent: false });
    // Desabilita controles de mapeamento de banco de dados por padrão
    this.form.get('entityName')?.disable({ emitEvent: false });
    this.form.get('fieldName')?.disable({ emitEvent: false });
    //  NOVO: Desabilita controles de imagem
    this.form.get('imageUrl')?.disable({ emitEvent: false });
    this.form.get('alt')?.disable({ emitEvent: false });
    this.form.get('width')?.disable({ emitEvent: false });
    this.form.get('height')?.disable({ emitEvent: false });
    this.form.get('borderRadius')?.disable({ emitEvent: false });
    this.form.get('enableLightbox')?.disable({ emitEvent: false });
    
    // Limpa array de colunas para todos os tipos de componente inicialmente
    this.columnsArray.clear();

    if (node.type === 'text') {
      this.form.get('required')?.enable({ emitEvent: false });
      this.form.get('minLength')?.enable({ emitEvent: false });
      this.form.get('maxLength')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      this.form.get('defaultValueText')?.enable({ emitEvent: false }); // NOVO: Valor padrão
      // Habilita controles de mapeamento de banco de dados
      this.form.get('entityName')?.enable({ emitEvent: false });
      this.form.get('fieldName')?.enable({ emitEvent: false });
      this.form.get('dataPath')?.enable({ emitEvent: false }); //  NOVO
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        minLength: (node.props as any).minLength ?? null,
        maxLength: (node.props as any).maxLength ?? null,
        backgroundColor: (node.props as any).backgroundColor ?? '',
        textColor: (node.props as any).textColor ?? '',
        defaultValueText: (node.props as any).defaultValue ?? '', // NOVO: Valor padrão
        minRows: null,
      }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    if (node.type === 'textarea') {
      this.form.get('required')?.enable({ emitEvent: false });
      this.form.get('minRows')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      this.form.get('defaultValueText')?.enable({ emitEvent: false }); // NOVO: Valor padrão
      // Habilita controles de mapeamento de banco de dados
      this.form.get('entityName')?.enable({ emitEvent: false });
      this.form.get('fieldName')?.enable({ emitEvent: false });
      this.form.get('dataPath')?.enable({ emitEvent: false }); //  NOVO
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        minRows: (node.props as any).minRows ?? null,
        backgroundColor: (node.props as any).backgroundColor ?? '',
        textColor: (node.props as any).textColor ?? '',
        defaultValueText: ((node.props as any).defaultValue !== undefined && (node.props as any).defaultValue !== null && (node.props as any).defaultValue !== false) 
          ? String((node.props as any).defaultValue) 
          : '', // CORRIGIDO: Converte para string e trata false/null/undefined
        minLength: null,
        maxLength: null,
      }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    if (node.type === 'button') {
      this.form.get('text')?.enable({ emitEvent: false });
      this.form.get('buttonType')?.enable({ emitEvent: false });
      this.form.get('size')?.enable({ emitEvent: false });
      this.form.get('disabled')?.enable({ emitEvent: false });
      this.form.get('loading')?.enable({ emitEvent: false });
      this.form.get('icon')?.enable({ emitEvent: false });
      this.form.get('actionType')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      // Habilitar controles de flowConfig
      this.form.get('flowConfig')?.enable({ emitEvent: false });
      this.form.get('flowDataMode')?.enable({ emitEvent: false });
      
      const flowConfig = (node.props as any).flowConfig || {};
      this.form.patchValue({
        ...base,
        text: (node.props as any).text ?? '',
        buttonType: (node.props as any).buttonType ?? 'default',
        size: (node.props as any).size ?? 'default',
        disabled: (node.props as any).disabled ?? false,
        loading: (node.props as any).loading ?? false,
        icon: (node.props as any).icon ?? '',
        actionType: (node.props as any).actionType ?? 'custom',
        backgroundColor: (node.props as any).backgroundColor ?? '',
        textColor: (node.props as any).textColor ?? '',
        flowConfig: {
          idConf: flowConfig.idConf ?? null,
          confName: flowConfig.confName ?? '',
          showCodeCompany: flowConfig.showCodeCompany ?? false,
          codeCompany: flowConfig.codeCompany ?? null,
          newMode: flowConfig.newMode ?? false
        },
        flowDataMode: (node.props as any).flowDataMode ?? 'full'
      }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    if (node.type === 'number') {
      this.form.get('required')?.enable({ emitEvent: false });
      this.form.get('min')?.enable({ emitEvent: false });
      this.form.get('max')?.enable({ emitEvent: false });
      this.form.get('step')?.enable({ emitEvent: false });
      this.form.get('precision')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      this.form.get('defaultValueNumber')?.enable({ emitEvent: false }); // Valor padrão
      // Habilita controles de mapeamento de banco de dados
      this.form.get('entityName')?.enable({ emitEvent: false });
      this.form.get('fieldName')?.enable({ emitEvent: false });
      this.form.get('dataPath')?.enable({ emitEvent: false });
      // Habilita controles de formatação (NG-ZORRO)
      this.form.get('decimalPlaces')?.enable({ emitEvent: false });
      this.form.get('formatThousands')?.enable({ emitEvent: false });
      this.form.get('thousandSeparator')?.enable({ emitEvent: false });
      this.form.get('decimalSeparator')?.enable({ emitEvent: false });
      this.form.get('addonBefore')?.enable({ emitEvent: false });
      this.form.get('addonAfter')?.enable({ emitEvent: false });
      // Debug: Log valores das props do node
      console.log('📥 Number selected - node.props:', {
        decimalPlaces: (node.props as any).decimalPlaces,
        formatThousands: (node.props as any).formatThousands,
        addonBefore: (node.props as any).addonBefore
      });

      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        min: (node.props as any).min ?? null,
        max: (node.props as any).max ?? null,
        step: (node.props as any).step ?? null,
        precision: (node.props as any).precision ?? null,
        backgroundColor: (node.props as any).backgroundColor ?? '',
        textColor: (node.props as any).textColor ?? '',
        defaultValueNumber: (node.props as any).defaultValue ?? null,
        // Propriedades de formatação
        decimalPlaces: (node.props as any).decimalPlaces ?? 2,
        formatThousands: (node.props as any).formatThousands ?? false,
        thousandSeparator: (node.props as any).thousandSeparator ?? '.',
        decimalSeparator: (node.props as any).decimalSeparator ?? ',',
        addonBefore: (node.props as any).addonBefore ?? '',
        addonAfter: (node.props as any).addonAfter ?? '',
      }, { emitEvent: false });
      
      this.cdr.markForCheck();
      return;
    }

    if (node.type === 'switch') {
      this.form.get('defaultValue')?.enable({ emitEvent: false });
      this.form.get('checkedChildren')?.enable({ emitEvent: false });
      this.form.get('unCheckedChildren')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      // Habilita controles de mapeamento de banco de dados
      this.form.get('entityName')?.enable({ emitEvent: false });
      this.form.get('fieldName')?.enable({ emitEvent: false });
      this.form.get('dataPath')?.enable({ emitEvent: false }); //  NOVO
      
      // Habilitar controles de tabControl e carregar abas disponíveis
      this.isInitializingTabControl = true; // Bloquear eventos durante inicialização
      
      this.form.get('tabControl.enabled')?.enable({ emitEvent: false });
      this.loadAvailableTabs();
      this.showTabControl = true;
      
      const tabControlData = (node.props as any).tabControl || {
        enabled: false,
        onTrueAction: 'show',
        onFalseAction: 'hide',
        targetTabs: [],
        mode: 'include'
      };
      
      // Fazer patch dos dados básicos primeiro
      this.form.patchValue({
        ...base,
        defaultValue: (node.props as any).defaultValue ?? false,
        checkedChildren: (node.props as any).checkedChildren ?? '',
        unCheckedChildren: (node.props as any).unCheckedChildren ?? '',
        backgroundColor: (node.props as any).backgroundColor ?? '',
        textColor: (node.props as any).textColor ?? ''
      }, { emitEvent: false });
      
      // Configurar tabControl separadamente para evitar eventos desnecessários
      this.form.get('tabControl')?.patchValue(tabControlData, { emitEvent: false });
      
      // Configurar estado inicial dos controles ANTES de desbloquear
      this.configureTabControlState(tabControlData.enabled);
      
      // Desbloquear eventos APÓS configurar o estado
      this.isInitializingTabControl = false;
      
      this.cdr.markForCheck();
      return;
    }

    if (node.type === 'simpletable' || node.type === 'table') {
      this.form.get('sorting')?.enable({ emitEvent: false });
      this.form.get('filtering')?.enable({ emitEvent: false });
      this.form.get('pagination')?.enable({ emitEvent: false });
      this.columnsArray.enable({ emitEvent: false });
      // Habilita controles de cor da tabela
      this.form.get('headerBackgroundColor')?.enable({ emitEvent: false });
      this.form.get('headerTextColor')?.enable({ emitEvent: false });
      this.form.get('rowBackgroundColor')?.enable({ emitEvent: false });
      this.form.get('rowTextColor')?.enable({ emitEvent: false });
      this.form.get('alternateRowColor')?.enable({ emitEvent: false });
      this.form.get('borderColor')?.enable({ emitEvent: false });
      this.form.get('hoverColor')?.enable({ emitEvent: false });
      
      // Desinscreve temporariamente para prevenir atualizações circulares durante população
      this.formSub?.unsubscribe();
      
      // Popula array de colunas
      const columns = (node.props as any).columns || [];
      columns.forEach((col: any) => {
        const columnForm = this.fb.group({
          key: this.fb.control(col.key || '', [Validators.required]),
          label: this.fb.control(col.label || '', [Validators.required]),
          type: this.fb.control(col.type || 'text'),
          sortable: this.fb.control(col.sortable || false),
          filterable: this.fb.control(col.filterable || false)
        });
        this.columnsArray.push(columnForm);
      });
      
      // Re-inscreve a mudanças do formulário após população
      this.bindFormChanges();
      
      this.form.patchValue({
        ...base,
        sorting: (node.props as any).sorting ?? false,
        filtering: (node.props as any).filtering ?? false,
        pagination: (node.props as any).pagination ?? true,
        headerBackgroundColor: (node.props as any).headerBackgroundColor ?? '',
        headerTextColor: (node.props as any).headerTextColor ?? '',
        rowBackgroundColor: (node.props as any).rowBackgroundColor ?? '',
        rowTextColor: (node.props as any).rowTextColor ?? '',
        alternateRowColor: (node.props as any).alternateRowColor ?? '',
        borderColor: (node.props as any).borderColor ?? '',
        hoverColor: (node.props as any).hoverColor ?? '',
      }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    if (node.type === 'datagrid') {
      // Habilita controles específicos do DataGrid
      this.form.get('label')?.enable({ emitEvent: false }); //  NOVO: Habilita label customizável
      this.form.get('dataPath')?.enable({ emitEvent: false });
      this.form.get('previewMode')?.enable({ emitEvent: false }); // ✅ NOVO
      this.form.get('enablePagination')?.enable({ emitEvent: false });
      this.form.get('pageSize')?.enable({ emitEvent: false });
      this.form.get('maxVisibleRows')?.enable({ emitEvent: false });
      this.form.get('enableSorting')?.enable({ emitEvent: false });
      this.form.get('enableFilters')?.enable({ emitEvent: false });
      this.form.get('enableCRUD')?.enable({ emitEvent: false });
      this.form.get('selectionMode')?.enable({ emitEvent: false });
      //  NOVO: Habilita controles de Master-Detail
      this.form.get('enableMasterDetail')?.enable({ emitEvent: false });
      this.form.get('masterDetailFormPanel')?.enable({ emitEvent: false });
      this.form.get('masterDetailFormPanels')?.enable({ emitEvent: false });
      this.form.get('masterDetailAutoGenerate')?.enable({ emitEvent: false });
      this.form.get('autoAddFirstRow')?.enable({ emitEvent: false });
      this.form.get('autoAddOrder')?.enable({ emitEvent: false });
      this.form.get('viewMode')?.enable({ emitEvent: false });
      this.form.get('drawerWidth')?.enable({ emitEvent: false });
      this.columnsArray.enable({ emitEvent: false });
      
      // Desinscreve temporariamente para prevenir atualizações circulares durante população
      this.formSub?.unsubscribe();
      
      // Popula array de colunas do DataGrid (incluindo propriedades ricas)
      const columns = (node.props as any).columns || [];
      columns.forEach((col: any) => {
        const columnForm = this.fb.group({
          field: this.fb.control(col.field || '', [Validators.required]),
          header: this.fb.control(col.header || '', [Validators.required]),
          type: this.fb.control(col.type || 'text'),
          dataPath: this.fb.control(col.dataPath || '', [Validators.required]),
          width: this.fb.control(col.width || ''),
          sortable: this.fb.control(col.sortable || false),
          filterable: this.fb.control(col.filterable || false),
          editable: this.fb.control(col.editable || false),
          searchProperties: this.fb.control(col.searchProperties || null),
          showKey: this.fb.control(col.showKey || false),
          fieldMappings: this.fb.control(col.fieldMappings || null),
          options: this.fb.control(col.options || null),
          multiple: this.fb.control(col.multiple || false),
          showSearch: this.fb.control(col.showSearch || false)
        });
        this.columnsArray.push(columnForm);
      });
      
      // Re-inscreve a mudanças do formulário após população
      this.bindFormChanges();
      
      this.form.patchValue({
        ...base,
        dataPath: (node.props as any).dataPath ?? '',
        previewMode: (node.props as any).previewMode ?? false, // ✅ NOVO
        enablePagination: (node.props as any).enablePagination ?? true,
        pageSize: (node.props as any).pageSize ?? 10,
        maxVisibleRows: (node.props as any).maxVisibleRows ?? 0,
        enableSorting: (node.props as any).enableSorting ?? true,
        enableFilters: (node.props as any).enableFilters ?? false,
        enableCRUD: (node.props as any).enableCRUD ?? false,
        selectionMode: (node.props as any).selectionMode ?? 'none',
        //  NOVO: Valores de Master-Detail
        enableMasterDetail: (node.props as any).enableMasterDetail ?? false,
        masterDetailFormPanel: (node.props as any).masterDetailFormPanel ?? null,
        masterDetailFormPanels: (node.props as any).masterDetailFormPanels ?? [],
        masterDetailAutoGenerate: (node.props as any).masterDetailAutoGenerate ?? false,
        autoAddFirstRow: (node.props as any).autoAddFirstRow ?? false,
        autoAddOrder: (node.props as any).autoAddOrder ?? 0,
        viewMode: (node.props as any).viewMode ?? 'inline',
        drawerWidth: (node.props as any).drawerWidth ?? 40,
      }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    // Tratamento de componentes Webhook V5
    if (this.builderService.isWebhookComponent(node.type)) {
      this.applyWebhookComponentProps(node);
      return;
    }

    if (node.type === 'spreadsheet') {
      // Habilita controles específicos da planilha
      this.form.get('showToolbar')?.enable({ emitEvent: false });
      this.form.get('showFormulaBar')?.enable({ emitEvent: false });
      this.form.get('showContextMenu')?.enable({ emitEvent: false });
      this.form.get('readonly')?.enable({ emitEvent: false });
      this.form.get('defaultRowHeight')?.enable({ emitEvent: false });
      this.form.get('defaultColumnWidth')?.enable({ emitEvent: false });
      this.form.get('maxRows')?.enable({ emitEvent: false });
      this.form.get('maxColumns')?.enable({ emitEvent: false });
      this.form.get('allowImportExport')?.enable({ emitEvent: false });
      this.form.get('enableFormulas')?.enable({ emitEvent: false });
      this.form.get('enableCharts')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('borderColor')?.enable({ emitEvent: false });
      this.form.get('headerBackgroundColor')?.enable({ emitEvent: false });
      this.form.get('headerTextColor')?.enable({ emitEvent: false });
      this.form.get('gridColor')?.enable({ emitEvent: false });
      
      this.form.patchValue({
        ...base,
        showToolbar: (node.props as any).showToolbar ?? true,
        showFormulaBar: (node.props as any).showFormulaBar ?? true,
        showContextMenu: (node.props as any).showContextMenu ?? true,
        readonly: (node.props as any).readonly ?? false,
        defaultRowHeight: (node.props as any).defaultRowHeight ?? 25,
        defaultColumnWidth: (node.props as any).defaultColumnWidth ?? 100,
        maxRows: (node.props as any).maxRows ?? 100,
        maxColumns: (node.props as any).maxColumns ?? 26,
        allowImportExport: (node.props as any).allowImportExport ?? true,
        enableFormulas: (node.props as any).enableFormulas ?? true,
        enableCharts: (node.props as any).enableCharts ?? false,
        backgroundColor: (node.props as any).backgroundColor ?? '',
        borderColor: (node.props as any).borderColor ?? '#d9d9d9',
        headerBackgroundColor: (node.props as any).headerBackgroundColor ?? '#f5f5f5',
        headerTextColor: (node.props as any).headerTextColor ?? '#262626',
        gridColor: (node.props as any).gridColor ?? '#e8e8e8',
      }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    // abas: habilita controles de cor e define campos base
    if (node.type === 'tabs') {
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      this.form.get('activeTabColor')?.enable({ emitEvent: false });
      this.form.get('activeTabTextColor')?.enable({ emitEvent: false });
      this.form.get('borderColor')?.enable({ emitEvent: false });
      this.form.get('contentBackgroundColor')?.enable({ emitEvent: false });
      this.form.get('contentTextColor')?.enable({ emitEvent: false });
      
      // Habilitar controle de ícone da aba ativa
      this.form.get('tabIcon')?.enable({ emitEvent: false });
      
      // Configurar aba ativa com ícones
      const activeTabId = this.builderService.getActiveTabId(node.id, (node.props as any).items);
      if (activeTabId) {
        const activeTab = ((node.props as any).items || []).find((t: any) => t.id === activeTabId);
        if (activeTab) {
          this.form.patchValue({ 
            label: activeTab.name,
            tabIcon: activeTab.icon || ''
          }, { emitEvent: false });
        }
        
        // Observar mudanças no ícone da aba
        const iconCtrl = this.form.get('tabIcon');
        iconCtrl?.valueChanges.subscribe(iconValue => {
          if (!this.currentSelectedId) return;
          this.builderService.updateTabsItemIcon(node.id, activeTabId, iconValue ?? '');
        });
      }
      
      // Barra de Contexto: nada a fazer no applySelectedNode — configuração via modal
    }

    if (node.type === 'panel') {
      this.form.get('name')?.enable({ emitEvent: false });
      this.form.get('active')?.enable({ emitEvent: false });
      this.form.get('disabled')?.enable({ emitEvent: false });
      this.form.get('order')?.enable({ emitEvent: false });
      this.form.get('collapsible')?.enable({ emitEvent: false });
      this.form.get('collapsed')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      this.form.get('textColor')?.enable({ emitEvent: false });
      this.form.get('panelBorderColor')?.enable({ emitEvent: false });
      this.form.get('panelHeaderBackgroundColor')?.enable({ emitEvent: false });
      this.form.get('panelHeaderTextColor')?.enable({ emitEvent: false });
      this.form.get('panelContentBackgroundColor')?.enable({ emitEvent: false });
    }

    //  NOVO: Propriedades específicas do Row
    if (node.type === 'row') {
      this.form.get('label')?.enable({ emitEvent: false });
      this.form.get('gap')?.enable({ emitEvent: false });
      this.form.get('alignItems')?.enable({ emitEvent: false });
      this.form.get('justifyContent')?.enable({ emitEvent: false });
      this.form.get('padding')?.enable({ emitEvent: false });
      this.form.get('backgroundColor')?.enable({ emitEvent: false });
      
      this.form.patchValue({
        label: (node.props as any).label || 'Linha',
        gap: (node.props as any).gap || '12px',
        alignItems: (node.props as any).alignItems || 'start',
        justifyContent: (node.props as any).justifyContent || 'start',
        padding: (node.props as any).padding || '8px',
        backgroundColor: (node.props as any).backgroundColor
      }, { emitEvent: false });
    }
    
    //  NOVO: Propriedades específicas do Image
    if (node.type === 'image') {
      console.log('🖼️ [Inspector] Configurando componente de imagem', node.props);
      this.form.get('imageUrl')?.enable({ emitEvent: false });
      this.form.get('alt')?.enable({ emitEvent: false });
      this.form.get('width')?.enable({ emitEvent: false });
      this.form.get('height')?.enable({ emitEvent: false });
      this.form.get('borderRadius')?.enable({ emitEvent: false });
      this.form.get('enableLightbox')?.enable({ emitEvent: false });
      this.form.get('dataPath')?.enable({ emitEvent: false });
      this.form.get('order')?.enable({ emitEvent: false }); // ✅ NOVO: Habilitar ordenação
      
      const imageProps = {
        ...base,
        imageUrl: (node.props as any).imageUrl ?? 'https://via.placeholder.com/300x200',
        alt: (node.props as any).alt ?? 'Imagem',
        width: (node.props as any).width ?? 300,
        height: (node.props as any).height ?? 200,
        borderRadius: (node.props as any).borderRadius ?? 0,
        enableLightbox: (node.props as any).enableLightbox ?? true,
        dataPath: (node.props as any).dataPath ?? ''
      };
      console.log('🖼️ [Inspector] Aplicando valores:', imageProps);
      this.form.patchValue(imageProps, { emitEvent: false });
      
      console.log('🖼️ [Inspector] Estado dos controles:', {
        imageUrl: this.form.get('imageUrl')?.disabled,
        alt: this.form.get('alt')?.disabled,
        width: this.form.get('width')?.disabled
      });
      
      this.updateDataPathPreview((node.props as any).dataPath);
      this.cdr.markForCheck();
      return;
    }
    
    // IMPORTANTE: Propriedades específicas do Panel
    const panelProps = node.type === 'panel' ? {
      name: (node.props as any).name ?? '',
      active: (node.props as any).active ?? false,
      disabled: (node.props as any).disabled ?? false,
      order: (node.props as any).order ?? 1,
      collapsible: (node.props as any).collapsible ?? true,
      collapsed: (node.props as any).collapsed ?? false,
    } : {};

    this.form.patchValue({ 
      ...base, 
      ...panelProps, // NOVA FUNCIONALIDADE: Incluir propriedades do Panel
      required: false, minLength: null, maxLength: null, minRows: null,
      min: null, max: null, step: null, precision: null,
      defaultValue: false, checkedChildren: '', unCheckedChildren: '',
      sorting: false, filtering: false, pagination: true,
      backgroundColor: (node.props as any).backgroundColor ?? '',
      textColor: (node.props as any).textColor ?? '',
      activeTabColor: (node.props as any).activeTabColor ?? '',
      activeTabTextColor: (node.props as any).activeTabTextColor ?? '',
      borderColor: (node.props as any).borderColor ?? '',
      contentBackgroundColor: (node.props as any).contentBackgroundColor ?? '',
      contentTextColor: (node.props as any).contentTextColor ?? '',
      hoverTabColor: (node.props as any).hoverTabColor ?? '',
      tabsContainerBackgroundColor: (node.props as any).tabsContainerBackgroundColor ?? '',
      tabsContainerBorderColor: (node.props as any).tabsContainerBorderColor ?? '',
      tabsContainerBorderRadius: (node.props as any).tabsContainerBorderRadius ?? '',
      tabsContainerPadding: (node.props as any).tabsContainerPadding ?? '',
      wrapperBackgroundColor: (node.props as any).wrapperBackgroundColor ?? '',
      wrapperBorderColor: (node.props as any).wrapperBorderColor ?? '',
      wrapperBorderRadius: (node.props as any).wrapperBorderRadius ?? '',
      wrapperPadding: (node.props as any).wrapperPadding ?? '',
      panelBorderColor: (node.props as any).panelBorderColor ?? '',
      panelHeaderBackgroundColor: (node.props as any).panelHeaderBackgroundColor ?? '',
      panelHeaderTextColor: (node.props as any).panelHeaderTextColor ?? '',
      panelContentBackgroundColor: (node.props as any).panelContentBackgroundColor ?? '',
    }, { emitEvent: false });
    // Para abas, controle de label edita título da aba ativa em vez do label do container
    const items = ((node.props as any)?.items ?? []) as Array<{ id: string; name: string }>;
    const activeTabId = this.builderService.getActiveTabId(node.id, items);
    if (activeTabId) {
      const active = items.find(i => i.id === activeTabId);
      if (active) {
        this.form.patchValue({ label: active.name }, { emitEvent: false });
      }
      const labelCtrl = this.form.get('label');
      labelCtrl?.valueChanges.subscribe(value => {
        if (!this.currentSelectedId) return;
        this.builderService.updateTabsItemName(node.id, activeTabId, value ?? '');
      });
    }
    this.cdr.markForCheck();
  }

  onBlur(controlName: string): void {
    const control = this.form.get(controlName);
    if (!control) return;
    if (control.invalid) {
      let key = 'builderScreen.validation.invalid';
      if (control.hasError('maxlength')) key = 'builderScreen.validation.maxLength';
      if (control.hasError('min')) key = 'builderScreen.validation.min';
      if (control.hasError('minMaxInvalid')) key = 'builderScreen.validation.minMaxInvalid';
      if (control.hasError('noColumns')) key = 'builderScreen.validation.noColumns';
      if (control.hasError('missingRequiredColumns')) key = 'builderScreen.validation.missingRequiredColumns';
      if (controlName === 'colSpan' && control.hasError('max')) key = 'builderScreen.validation.colspanMax';
      this.translate.get(key).subscribe(msg => this.message.error(msg));
    }
    
    //  NOVO: Se for dataPath, atualizar preview
    if (controlName === 'dataPath') {
      const dataPath = this.form.get('dataPath')?.value;
      this.updateDataPathPreview(dataPath);
    }
  }

  // Validador customizado para validação min/max do componente Number
  private minMaxValidator(control: any): { [key: string]: any } | null {
    if (!control.value || control.value === null) {
      return null;
    }

    const minControl = this.form?.get('min');
    const maxControl = this.form?.get('max');
    
    if (minControl && maxControl) {
      const minValue = minControl.value;
      const maxValue = maxControl.value;
      
      if (minValue !== null && maxValue !== null && minValue >= maxValue) {
        return { 'minMaxInvalid': { min: minValue, max: maxValue } };
      }
    }
    
    return null;
  }

  // Validador para colunas obrigatórias da tabela
  private tableColumnsValidator(): { [key: string]: any } | null {
    if (this.currentType !== 'table' && this.currentType !== 'simpletable') {
      return null;
    }

    const columns = this.columnsArray.value;
    if (!columns || columns.length === 0) {
      return { 'noColumns': true };
    }

    const hasRequiredColumns = columns.some((col: any) => col.key && col.label);
    if (!hasRequiredColumns) {
      return { 'missingRequiredColumns': true };
    }

    return null;
  }

  get columnsArray(): FormArray {
    return this.form.get('columns') as FormArray;
  }

  addColumn(): void {
    // Desinscreve temporariamente para prevenir atualizações circulares
    this.formSub?.unsubscribe();
    
    const columnForm = this.fb.group({
      key: this.fb.control(`column_${this.columnsArray.length + 1}`, [Validators.required]),
      label: this.fb.control(`Column ${this.columnsArray.length + 1}`, [Validators.required]),
      type: this.fb.control('text'),
      sortable: this.fb.control(false),
      filterable: this.fb.control(false)
    });
    this.columnsArray.push(columnForm);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
  }

  removeColumn(index: number): void {
    // Desinscreve temporariamente para prevenir atualizações circulares
    this.formSub?.unsubscribe();
    
    this.columnsArray.removeAt(index);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
  }

  // DataGrid column management
  get dataGridColumnsArray(): FormArray {
    return this.form.get('columns') as FormArray;
  }

  addDataGridColumn(): void {
    console.log('➕ Adicionando coluna manual ao DataGrid...');
    
    // Desinscreve temporariamente para prevenir atualizações circulares
    this.formSub?.unsubscribe();
    
    const columnForm = this.fb.group({
      field: this.fb.control(`field_${this.dataGridColumnsArray.length + 1}`, [Validators.required]),
      header: this.fb.control(`Header ${this.dataGridColumnsArray.length + 1}`, [Validators.required]),
      type: this.fb.control('text'),
      dataPath: this.fb.control(`field_${this.dataGridColumnsArray.length + 1}`, [Validators.required]),
      width: this.fb.control(''),
      sortable: this.fb.control(false),
      filterable: this.fb.control(false),
      editable: this.fb.control(false)
    });
    this.dataGridColumnsArray.push(columnForm);
    
    console.log('➕ Total de colunas agora:', this.dataGridColumnsArray.length);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
    
    // 🔥 FORÇAR atualização imediata das props
    this.forceUpdateDataGridProps();
  }

  removeDataGridColumn(index: number): void {
    console.log('➖ Removendo coluna', index, 'do DataGrid...');
    
    // Desinscreve temporariamente para prevenir atualizações circulares
    this.formSub?.unsubscribe();
    
    this.dataGridColumnsArray.removeAt(index);
    
    console.log('➖ Total de colunas agora:', this.dataGridColumnsArray.length);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
    
    // 🔥 FORÇAR atualização imediata das props
    this.forceUpdateDataGridProps();
  }

  //  NOVO: Dropdown options management
  get dropdownOptionsArray(): FormArray {
    return this.form.get('dropdownOptions') as FormArray;
  }

  addDropdownOption(): void {
    // Desinscreve temporariamente para prevenir atualizações circulares
    this.formSub?.unsubscribe();
    
    const optionForm = this.fb.group({
      value: this.fb.control('', [Validators.required]),
      label: this.fb.control('', [Validators.required]),
      isDefault: this.fb.control(false)
    });
    this.dropdownOptionsArray.push(optionForm);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
  }

  removeDropdownOption(index: number): void {
    // Desinscreve temporariamente para prevenir atualizações circulares
    this.formSub?.unsubscribe();
    
    this.dropdownOptionsArray.removeAt(index);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
  }

  private populateDropdownOptions(options: Array<{ value: any; label: string; isDefault?: boolean }>): void {
    // ✅ CORREÇÃO: Desinscreve temporariamente para prevenir loop infinito
    this.formSub?.unsubscribe();
    
    this.dropdownOptionsArray.clear();
    if (options && options.length > 0) {
      options.forEach(option => {
        const optionForm = this.fb.group({
          value: this.fb.control(option.value || '', [Validators.required]),
          label: this.fb.control(option.label || '', [Validators.required]),
          isDefault: this.fb.control(option.isDefault || false)
        });
        this.dropdownOptionsArray.push(optionForm);
      });
    }
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
  }

  //  NOVO: Abre modal para configurar opções do dropdown
  openDropdownOptionsModal(): void {
    const currentOptions = this.dropdownOptionsArray.value || [];
    
    const modalRef = this.modal.create({
      nzTitle: null,
      nzContent: DropdownOptionsModalComponent,
      nzFooter: null,
      nzWidth: 600,
      nzClassName: 'dropdown-options-modal-container'
    });

    // Passa opções atuais para o modal
    modalRef.componentInstance!.initialOptions = currentOptions;

    // Aguarda resultado do modal
    modalRef.afterClose.subscribe((result: Array<{ value: any; label: string; isDefault?: boolean }> | undefined) => {
      if (result && this.currentSelectedId) {
        // Atualiza FormArray com novas opções
        this.formSub?.unsubscribe();
        this.populateDropdownOptions(result);
        this.bindFormChanges();
        
        // 🔥 IMPORTANTE: Calcular defaultValue baseado na opção marcada como isDefault
        const defaultOption = result.find((opt: any) => opt.isDefault);
        const defaultValue = defaultOption ? defaultOption.value : null;
        
        // 🔥 IMPORTANTE: Força salvamento imediato das opções no BuilderService
        const currentFormValues = this.form.value;
        this.builderService.updateNodeProps(this.currentSelectedId, {
          ...currentFormValues,
          options: result,
          defaultValue: defaultValue  // ✅ NOVO: Salva o valor default calculado
        } as any);
        
        // Força atualização do componente
        this.cdr.detectChanges();
        
        console.log('✅ Opções do dropdown salvas:', result, 'defaultValue:', defaultValue);
      }
    });
  }

  // NOVO: Abre modal para configurar formatação do Number
  openNumberFormatModal(): void {
    // Debug: Log valores do formulário
    console.log('🔧 openNumberFormatModal - Form values:', {
      decimalPlaces: this.form.get('decimalPlaces')?.value,
      formatThousands: this.form.get('formatThousands')?.value,
      thousandSeparator: this.form.get('thousandSeparator')?.value,
      decimalSeparator: this.form.get('decimalSeparator')?.value,
      addonBefore: this.form.get('addonBefore')?.value,
      addonAfter: this.form.get('addonAfter')?.value
    });

    const currentConfig: NumberFormatModalConfig = {
      decimalPlaces: this.form.get('decimalPlaces')?.value ?? 2,
      formatThousands: this.form.get('formatThousands')?.value ?? false,
      thousandSeparator: this.form.get('thousandSeparator')?.value ?? '.',
      decimalSeparator: this.form.get('decimalSeparator')?.value ?? ',',
      addonBefore: this.form.get('addonBefore')?.value ?? '',
      addonAfter: this.form.get('addonAfter')?.value ?? ''
    };

    console.log('🔧 openNumberFormatModal - currentConfig:', currentConfig);

    const modalRef = this.modal.create({
      nzTitle: null,
      nzContent: NumberFormatModalComponent,
      nzFooter: null,
      nzWidth: 500,
      nzClassName: 'number-format-modal-container'
    });

    // Passa configuração atual para o modal
    modalRef.componentInstance!.initialConfig = currentConfig;

    // Aguarda resultado do modal
    modalRef.afterClose.subscribe((result: NumberFormatModalConfig | undefined) => {
      if (result && this.currentSelectedId) {
        // Atualiza formulário local
        this.formSub?.unsubscribe();
        this.form.patchValue({
          decimalPlaces: result.decimalPlaces,
          formatThousands: result.formatThousands,
          thousandSeparator: result.thousandSeparator,
          decimalSeparator: result.decimalSeparator,
          addonBefore: result.addonBefore,
          addonAfter: result.addonAfter
        }, { emitEvent: false });
        this.bindFormChanges();

        // Salva no BuilderService
        const propsToSave = {
          decimalPlaces: result.decimalPlaces,
          formatThousands: result.formatThousands,
          thousandSeparator: result.thousandSeparator,
          decimalSeparator: result.decimalSeparator,
          addonBefore: result.addonBefore,
          addonAfter: result.addonAfter
        };
        
        console.log('💾 Saving number format props to BuilderService:', propsToSave);
        
        // ✅ CORREÇÃO: Definir janela de tempo para pular múltiplas emissões do selectedNode$
        this._skipUntilTimestamp = Date.now() + 500; // 500ms de janela
        
        this.builderService.updateNodeProps(this.currentSelectedId, propsToSave as any);

        // Força atualização do componente
        this.cdr.detectChanges();

        console.log('✅ Formatação do Number salva:', result);
      }
    });
  }

  /**
   * 🔥 Força atualização imediata das props do DataGrid
   * Usado quando addColumn/removeColumn não dispara valueChanges automaticamente
   */
  private forceUpdateDataGridProps(): void {
    if (!this.currentSelectedId || this.currentType !== 'datagrid') {
      return;
    }

    console.log('🔥 Forçando atualização das props do DataGrid...');

    const formValue = this.form.value;
    const columns = (formValue.columns as any[])?.map(col => {
      const mapped: any = {
        field: col.field || '',
        header: col.header || '',
        type: col.type || 'text',
        dataPath: col.dataPath || '',
        width: col.width || '',
        sortable: col.sortable || false,
        filterable: col.filterable || false,
        editable: col.editable || false
      };
      if (col.searchProperties) mapped.searchProperties = col.searchProperties;
      if (col.showKey) mapped.showKey = col.showKey;
      if (col.fieldMappings) mapped.fieldMappings = col.fieldMappings;
      if (col.options) mapped.options = col.options;
      if (col.multiple) mapped.multiple = col.multiple;
      if (col.showSearch) mapped.showSearch = col.showSearch;
      return mapped;
    }) || [];

    console.log('🔥 Colunas a serem aplicadas:', columns.length);
    console.log('🔥 Colunas:', columns);

    this.builderService.updateNodeProps(this.currentSelectedId, {
      label: formValue.label,
      placeholder: formValue.placeholder,
      rowSpan: formValue.rowSpan,
      colSpan: formValue.colSpan,
      entityName: formValue.entityName,
      fieldName: formValue.fieldName,
      dataPath: formValue.dataPath,
      enablePagination: formValue.enablePagination,
      pageSize: formValue.pageSize,
      maxVisibleRows: formValue.maxVisibleRows,
      enableSorting: formValue.enableSorting,
      enableFilters: formValue.enableFilters,
      enableCRUD: formValue.enableCRUD,
      selectionMode: formValue.selectionMode,
      columns
    } as any);

    this.cdr.markForCheck();
    console.log('✅ Props do DataGrid atualizadas!');
  }

  // ✅ Smart Column Builder
  smartColumnBuilderVisible = false;

  openSmartColumnBuilder(): void {
    this.smartColumnBuilderVisible = true;
  }

  getCurrentDataGridColumns(): any[] {
    if (this.currentType !== 'datagrid') {
      return [];
    }
    return this.dataGridColumnsArray.value || [];
  }

  /**
   * Retorna os nodes do formulário conectados ao DataGrid atual (dos painéis master-detail).
   * Usado pelo Smart Column Builder para inferir tipos ricos (search, dropdown).
   */
  getFormNodesForCurrentGrid(): any[] {
    if (this.currentType !== 'datagrid') return [];
    const props = this.form?.value as any;
    const panelIds: string[] = [];
    if (props?.masterDetailFormPanel) panelIds.push(props.masterDetailFormPanel);
    if (props?.masterDetailFormPanels) panelIds.push(...props.masterDetailFormPanels);
    if (panelIds.length === 0) return [];
    
    const allNodes = this.builderService.currentState.nodes;
    return allNodes.filter(n => {
      if (n.type === 'panel' || n.type === 'tabs' || n.type === 'datagrid' || n.type === 'row') return false;
      return panelIds.includes(n.parentId as string);
    });
  }

  // ✅ Column Manager
  columnManagerVisible = false;

  openColumnManager(): void {
    this.columnManagerVisible = true;
  }

  onColumnsChanged(columns: any[]): void {
    console.log('📋 Colunas recebidas do Column Manager:', columns);
    
    while (this.dataGridColumnsArray.length > 0) {
      this.dataGridColumnsArray.removeAt(0);
    }
    
    columns.forEach(column => {
      this.dataGridColumnsArray.push(
        this.fb.group({
          field: [column.field],
          header: [column.header],
          type: [column.type || 'text'],
          dataPath: [column.dataPath],
          width: [column.width || 'auto'],
          sortable: [column.sortable ?? true],
          filterable: [column.filterable ?? false],
          editable: [column.editable ?? false],
          searchProperties: [column.searchProperties || null],
          showKey: [column.showKey || false],
          fieldMappings: [column.fieldMappings || null],
          options: [column.options || null],
          multiple: [column.multiple || false],
          showSearch: [column.showSearch || false]
        })
      );
    });
    
    console.log('✅ Total de colunas após aplicação:', this.dataGridColumnsArray.length);
    
    this.forceUpdateDataGridProps();
  }

  onSmartColumnsGenerated(result: { columns: any[], dataPath: string }): void {
    const { columns, dataPath } = result;
    
    console.log('📊 ===== SMART COLUMN BUILDER RESULT =====');
    console.log('📊 Colunas geradas:', columns.length);
    console.log('📊 Data Path principal:', dataPath);
    console.log('📊 Colunas detalhadas:', columns);
    console.log('📊 Current Type:', this.currentType);
    console.log('📊 Selected Component:', this.currentSelectedId);
    
    if (this.currentType !== 'datagrid') {
      console.error('❌ Componente selecionado não é DataGrid!', this.currentType);
      this.message.error('Por favor, selecione um DataGrid antes de adicionar colunas');
      return;
    }
    
    // Desinscreve temporariamente
    this.formSub?.unsubscribe();
    
    // ✅ Aplicar Data Path principal no form
    if (dataPath) {
      console.log('📊 Aplicando dataPath no form:', dataPath);
      this.form.get('dataPath')?.setValue(dataPath);
      console.log('📊 DataPath no form após aplicar:', this.form.get('dataPath')?.value);
    }
    
    // Limpar colunas existentes
    console.log('📊 Limpando', this.dataGridColumnsArray.length, 'colunas existentes...');
    while (this.dataGridColumnsArray.length > 0) {
      this.dataGridColumnsArray.removeAt(0);
    }
    
    // Adicionar novas colunas geradas
    console.log('📊 Adicionando', columns.length, 'novas colunas...');
    columns.forEach((column, index) => {
      const columnForm = this.fb.group({
        field: this.fb.control(column.field, [Validators.required]),
        header: this.fb.control(column.header, [Validators.required]),
        type: this.fb.control(column.type),
        dataPath: this.fb.control(column.dataPath, [Validators.required]),
        width: this.fb.control(column.width || ''),
        sortable: this.fb.control(column.sortable ?? true),
        filterable: this.fb.control(column.filterable ?? false),
        editable: this.fb.control(column.editable ?? false),
        searchProperties: this.fb.control(column.searchProperties || null),
        showKey: this.fb.control(column.showKey || false),
        fieldMappings: this.fb.control(column.fieldMappings || null),
        options: this.fb.control(column.options || null),
        multiple: this.fb.control(column.multiple || false),
        showSearch: this.fb.control(column.showSearch || false)
      });
      this.dataGridColumnsArray.push(columnForm);
      console.log(`📊 Coluna ${index + 1} adicionada:`, column.field, '→', column.dataPath);
    });
    
    console.log('📊 Total de colunas no form agora:', this.dataGridColumnsArray.length);
    console.log('📊 Form válido?', this.form.valid);
    console.log('📊 Form value:', this.form.value);
    
    // Re-inscreve a mudanças do formulário
    this.bindFormChanges();
    
    // Forçar aplicação ao nó selecionado
    if (this.currentSelectedId) {
      console.log('📊 Forçando atualização do nó:', this.currentSelectedId);
      // Buscar nó atual e atualizar suas props
      const currentState = this.builderService.currentState;
      const currentNode = currentState.nodes.find(n => n.id === this.currentSelectedId);
      if (currentNode) {
        const formValue = this.form.value;
        const updatedNode = {
          ...currentNode,
          props: { ...currentNode.props, ...formValue }
        };
        this.builderService.updateNode(updatedNode);
        console.log('📊 Nó atualizado com sucesso');
      }
    }
    
    // Mensagem de sucesso com dataPath
    this.message.success(`✅ ${columns.length} colunas adicionadas! Data Path: ${dataPath}`);
    this.cdr.markForCheck();
    
    console.log('📊 ===== FIM SMART COLUMN BUILDER =====');
  }

  trackByIndex(index: number): number {
    return index;
  }

  // Métodos de controle da Grade V3
  adjustRowSpan(delta: number): void {
    const currentValue = this.form.get('rowSpan')?.value || 1;
    const newValue = Math.max(1, Math.min(50, currentValue + delta));
    this.form.get('rowSpan')?.setValue(newValue);
  }

  adjustColSpan(delta: number): void {
    const currentValue = this.form.get('colSpan')?.value || 1;
    const newValue = Math.max(1, Math.min(24, currentValue + delta));
    this.form.get('colSpan')?.setValue(newValue);
  }

  // Métodos auxiliares de componente Webhook V5
  private getWebhookSpecificProps(values: any): any {
    const props: any = {
      required: values.required || false,
      isDisabled: values.isDisabled ?? false // ✅ Propagar isDisabled
    };

    if (this.currentType === 'date') {
      props.dateFormat = values.dateFormat || 'DD/MM/YYYY';
      props.showTime = values.showTime || false;
      props.defaultValue = values.defaultValueText || ''; // NOVO: Valor padrão
    } else if (this.currentType === 'select') {
      props.multiple = values.multiple || false;
      props.showSearch = values.showSearch || false;
    } else if (this.currentType === 'dropdown') {
      // Coletar opções do FormArray, incluindo isDefault
      const options = (values.dropdownOptions as any[])?.map((opt: any) => ({
        value: opt.value,
        label: opt.label,
        isDefault: opt.isDefault || false
      })) || [];
      props.options = options;
      props.multiple = values.multiple || false;
      props.showSearch = values.showSearch || false;
      props.backgroundColor = values.backgroundColor || '';
      props.textColor = values.textColor || '';
      // Calcular defaultValue baseado na opção marcada como default
      const defaultOption = options.find((opt: any) => opt.isDefault);
      props.defaultValue = defaultOption ? defaultOption.value : null;
    } else if (this.currentType === 'querySelect') {
      props.condition = values.condition || '';
      props.showKey = values.showKey || false;
      props.entityName = values.entityName || '';
    } else if (this.currentType === 'search') {
      props.showKey = values.showKey || false;
      //  Estrutura correta do searchProperties
      props.searchProperties = {
        entityName: values.searchEntityName || '',
        fields: {
          keyField: values.searchKeyField || '',
          valueField: values.searchValueField || ''
        }
      };
    } else if (this.currentType === 'divider') {
      props.dashed = values.dashed || false;
      props.type = values.dividerType || 'horizontal';
      props.orientation = values.orientation || 'center';
      props.color = values.color || '#d9d9d9';
    } else if (this.currentType === 'section') {
      props.title = values.title || '';
      props.collapsible = values.collapsible || false;
      props.collapsed = values.collapsed || false;
    }

    return props;
  }

  private applyWebhookComponentProps(node: BuilderNode): void {
    const base = {
      label: (node.props as any).label ?? '',
      placeholder: (node.props as any).placeholder ?? '',
      rowSpan: (node.props as any).rowSpan ?? 1,
      colSpan: (node.props as any).colSpan ?? 24,
      order: (node.props as any).order ?? 0, // ✅ NOVO: Ordem do componente
      isDisabled: (node.props as any).isDisabled ?? false, // ✅ Carregar isDisabled do node
      dataPath: (node.props as any).dataPath ?? '', //  NOVO
    };

    // Habilita controles comuns
    this.form.get('required')?.enable({ emitEvent: false });
    this.form.get('isDisabled')?.enable({ emitEvent: false }); // ✅ Habilitar isDisabled para todos os webhook components
    this.form.get('dataPath')?.enable({ emitEvent: false }); //  NOVO
    this.form.get('order')?.enable({ emitEvent: false }); // ✅ NOVO: Habilitar ordenação para todos os tipos

    // Habilita controles específicos baseado no tipo de componente
    if (node.type === 'date') {
      this.form.get('dateFormat')?.enable({ emitEvent: false });
      this.form.get('showTime')?.enable({ emitEvent: false });
      this.form.get('defaultValueText')?.enable({ emitEvent: false }); // NOVO: Valor padrão
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        dateFormat: (node.props as any).dateFormat ?? 'DD/MM/YYYY',
        showTime: (node.props as any).showTime ?? false,
        defaultValueText: (node.props as any).defaultValue ?? '', // NOVO: Valor padrão
      }, { emitEvent: false });
    } else if (node.type === 'select') {
      this.form.get('multiple')?.enable({ emitEvent: false });
      this.form.get('showSearch')?.enable({ emitEvent: false });
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        multiple: (node.props as any).multiple ?? false,
        showSearch: (node.props as any).showSearch ?? false,
      }, { emitEvent: false });
    } else if (node.type === 'dropdown') {
      this.form.get('multiple')?.enable({ emitEvent: false });
      this.form.get('showSearch')?.enable({ emitEvent: false });
      this.form.get('order')?.enable({ emitEvent: false }); // ✅ NOVO: Habilitar ordenação
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        multiple: (node.props as any).multiple ?? false,
        showSearch: (node.props as any).showSearch ?? false,
        backgroundColor: (node.props as any).backgroundColor ?? '',
        textColor: (node.props as any).textColor ?? ''
      }, { emitEvent: false });
      // Popular opções existentes
      this.populateDropdownOptions((node.props as any).options || []);
      // ✅ Forçar detecção de mudanças para atualizar UI
      this.cdr.markForCheck();
      return; // ✅ Retornar para evitar processar blocos subsequentes
    } else if (node.type === 'querySelect') {
      this.form.get('condition')?.enable({ emitEvent: false });
      this.form.get('showKey')?.enable({ emitEvent: false });
      this.form.get('entityName')?.enable({ emitEvent: false });
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        condition: (node.props as any).condition ?? '',
        showKey: (node.props as any).showKey ?? false,
        entityName: (node.props as any).entityName ?? '',
      }, { emitEvent: false });
    } else if (node.type === 'search') {
      this.form.get('showKey')?.enable({ emitEvent: false });
      this.form.get('searchEntityName')?.enable({ emitEvent: false });
      this.form.get('searchKeyField')?.enable({ emitEvent: false });
      this.form.get('searchValueField')?.enable({ emitEvent: false });
      
      const searchProps = (node.props as any).searchProperties;
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        showKey: (node.props as any).showKey ?? false,
        searchEntityName: searchProps?.entityName ?? '',
        searchKeyField: searchProps?.fields?.keyField ?? '',
        searchValueField: searchProps?.fields?.valueField ?? '',
      }, { emitEvent: false });
    } else if (node.type === 'divider') {
      this.form.get('dashed')?.enable({ emitEvent: false });
      this.form.get('dividerType')?.enable({ emitEvent: false });
      this.form.get('orientation')?.enable({ emitEvent: false });
      this.form.get('color')?.enable({ emitEvent: false });
      this.form.patchValue({
        ...base,
        dashed: (node.props as any).dashed ?? false,
        dividerType: (node.props as any).type ?? 'horizontal',
        orientation: (node.props as any).orientation ?? 'center',
        color: (node.props as any).color ?? '#d9d9d9',
      }, { emitEvent: false });
    } else if (node.type === 'section') {
      this.form.get('title')?.enable({ emitEvent: false });
      this.form.get('collapsible')?.enable({ emitEvent: false });
      this.form.get('collapsed')?.enable({ emitEvent: false });
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
        title: (node.props as any).title ?? '',
        collapsible: (node.props as any).collapsible ?? false,
        collapsed: (node.props as any).collapsed ?? false,
      }, { emitEvent: false });
    } else {
      // Padrão para outros componentes webhook (keyValue, keyValueObject, tag)
      this.form.patchValue({
        ...base,
        required: (node.props as any).required ?? false,
      }, { emitEvent: false });
    }

    this.cdr.markForCheck();
  }

  // Método auxiliar para verificar se componente é webhook
  isWebhookComponent(type: BuilderComponentType): boolean {
    return this.builderService.isWebhookComponent(type);
  }

  // Métodos do Inspetor Aprimorado V5
  updateInspectorForComponent(componentType: BuilderComponentType | null): void {
    this.selectedComponentType = componentType;
    
    if (componentType) {
      this.essentialProperties = this.inspectorConfig.getEssentialProperties(componentType);
      this.advancedProperties = this.inspectorConfig.getAdvancedProperties(componentType);
      this.propertyGroups = this.inspectorConfig.getPropertyGroups();
      this.componentPresets = this.inspectorConfig.getPresetsForComponent(componentType);
    } else {
      this.essentialProperties = [];
      this.advancedProperties = [];
      this.propertyGroups = [];
      this.componentPresets = [];
    }
    
    this.cdr.markForCheck();
  }

  getEssentialProperties(componentType: BuilderComponentType): InspectorProperty[] {
    return this.inspectorConfig.getEssentialProperties(componentType);
  }

  getAdvancedProperties(componentType: BuilderComponentType): InspectorProperty[] {
    return this.inspectorConfig.getAdvancedProperties(componentType);
  }

  hasAdvancedProperties(componentType: BuilderComponentType): boolean {
    return this.inspectorConfig.hasAdvancedProperties(componentType);
  }

  getAdvancedHeaderText(componentType: BuilderComponentType): string {
    return this.inspectorConfig.getAdvancedHeaderText(componentType);
  }

  getPropertiesByGroup(componentType: BuilderComponentType): Map<string, InspectorProperty[]> {
    return this.inspectorConfig.getPropertiesByGroup(componentType);
  }

  // Gerenciamento de presets
  applyPreset(presetId: string): void {
    const preset = this.inspectorConfig.getPreset(presetId);
    if (!preset || !this.currentSelectedId) return;

    // Atualiza o nó com propriedades do preset
    this.builderService.updateNodeProps(this.currentSelectedId, preset.props as any);
    
    // Atualiza o formulário para refletir os novos valores
    this.form.patchValue(preset.props, { emitEvent: false });
    
    this.showPresetSelector = false;
    this.message.success(`Preset "${preset.name}" aplicado com sucesso!`);
    this.cdr.markForCheck();
  }

  togglePresetSelector(): void {
    this.showPresetSelector = !this.showPresetSelector;
    this.cdr.markForCheck();
  }

  onInspectorSearch(term: string): void {
    const panels = document.querySelectorAll('.inspector-container nz-collapse-panel');
    if (!term) {
      panels.forEach(p => (p as HTMLElement).style.display = '');
      return;
    }
    const lower = term.toLowerCase();
    panels.forEach(p => {
      const text = (p as HTMLElement).innerText?.toLowerCase() || '';
      (p as HTMLElement).style.display = text.includes(lower) ? '' : 'none';
    });
  }

  // Sistema de ajuda de propriedades
  getPropertyHelp(propertyKey: string): any {
    return this.inspectorConfig.getPropertyHelp(propertyKey);
  }

  // Criação dinâmica de controles de formulário
  createFormControlForProperty(property: InspectorProperty): any {
    const validators = [];
    
    if (property.type === 'text' || property.type === 'textarea') {
      validators.push(Validators.maxLength(256));
    }
    
    if (property.type === 'number') {
      if (property.min !== undefined) validators.push(Validators.min(property.min));
      if (property.max !== undefined) validators.push(Validators.max(property.max));
    }

    const defaultValue = this.getDefaultValueForProperty(property);
    return this.fb.control({ value: defaultValue, disabled: true }, validators);
  }

  private getDefaultValueForProperty(property: InspectorProperty): any {
    switch (property.type) {
      case 'checkbox':
        return false;
      case 'number':
        return null;
      case 'text':
      case 'textarea':
      case 'select':
      case 'color':
        return '';
      default:
        return null;
    }
  }

  // Validação aprimorada de propriedades
  isPropertyVisible(property: InspectorProperty): boolean {
    if (!this.selectedComponentType) return false;
    return property.component.includes(this.selectedComponentType);
  }

  isPropertyEnabled(property: InspectorProperty): boolean {
    const control = this.form.get(property.key);
    return control ? control.enabled : false;
  }

  // Métodos de validação de cor
  isValidHexColor(color: string): boolean {
    if (!color) return true; // Vazio é válido (significa sem cor)
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex.test(color);
  }

  validateColorInput(controlName: string): void {
    const control = this.form.get(controlName);
    if (control && control.value && !this.isValidHexColor(control.value)) {
      control.setErrors({ invalidColor: true });
      this.message.error(this.translate.instant('builderScreen.colorPicker.validation.invalid'));
    } else if (control && control.errors?.['invalidColor']) {
      control.setErrors(null);
    }
  }

  // Seleção de preset de cor
  // Métodos de preset de cor removidos para UI simplificada

  // Métodos auxiliares para template
  hasPropertiesInGroup(groupId: string): boolean {
    if (!this.selectedComponentType) return false;
    const groupProperties = this.inspectorConfig.getPropertiesByGroup(this.selectedComponentType);
    const properties = groupProperties.get(groupId) || [];
    return properties.some(prop => prop.advanced);
  }

  getPropertiesInGroup(groupId: string): InspectorProperty[] {
    if (!this.selectedComponentType) return [];
    const groupProperties = this.inspectorConfig.getPropertiesByGroup(this.selectedComponentType);
    const properties = groupProperties.get(groupId) || [];
    return properties.filter(prop => prop.advanced);
  }

  // método optimizePosition() removido - movido para backlog V4

  // Métodos para controle de abas do Switch
  
  /**
   * Carrega as abas disponíveis no builder atual
   */
  private loadAvailableTabs(): void {
    this.availableTabs = this.tabControlService.getAvailableTabs();
  }

  /**
   * Manipula a mudança do toggle "Habilitar controle de abas"
   */
  onTabControlEnabledChange(enabled: boolean): void {
    // Se estamos inicializando, ignorar eventos do template
    if (this.isInitializingTabControl) {
      return;
    }
    
    // Usar o mesmo método de configuração
    this.configureTabControlState(enabled);
    
    // Forçar detecção de mudança
    this.cdr.markForCheck();
  }

  /**
   * Configura o estado inicial dos controles de tabControl (sem depender de eventos)
   */
  private configureTabControlState(enabled: boolean): void {
    const tabControlGroup = this.form.get('tabControl');
    if (!tabControlGroup) return;

    if (enabled) {
      tabControlGroup.get('onTrueAction')?.enable({ emitEvent: false });
      tabControlGroup.get('onFalseAction')?.enable({ emitEvent: false });
      tabControlGroup.get('targetTabs')?.enable({ emitEvent: false });
      tabControlGroup.get('mode')?.enable({ emitEvent: false });
      this.loadAvailableTabs();
    } else {
      tabControlGroup.get('onTrueAction')?.disable({ emitEvent: false });
      tabControlGroup.get('onFalseAction')?.disable({ emitEvent: false });
      tabControlGroup.get('targetTabs')?.disable({ emitEvent: false });
      tabControlGroup.get('mode')?.disable({ emitEvent: false });
    }
  }

  /**
   * Valida se pelo menos uma aba foi selecionada quando controle está habilitado
   */
  validateTabControlTargets(): boolean {
    const tabControlGroup = this.form.get('tabControl');
    if (!tabControlGroup) return true;

    const enabled = tabControlGroup.get('enabled')?.value;
    const targetTabs = tabControlGroup.get('targetTabs')?.value || [];

    if (enabled && targetTabs.length === 0) {
      return false;
    }

    return true;
  }

  /**
   * Métodos auxiliares para ícones de aba
   */
  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }
  
  getIconsByCategory(category: string): IconOption[] {
    return this.iconManagerService.getIconsByCategory(category);
  }

  getIconPreview(iconValue: string): string | null {
    return this.iconManagerService.getIconPath(iconValue);
  }

  /**
   * ⭐ NOVO: Verifica se é um ícone customizado (do MKTPICONS)
   */
  isCustomIcon(iconValue: string | null | undefined): boolean {
    if (!iconValue) return false;
    return iconValue.startsWith('custom:');
  }

  /**
   * ⭐ NOVO: Extrai o nome do ícone customizado (remove o prefixo "custom:")
   */
  getCustomIconName(iconValue: string | null | undefined): string {
    if (!iconValue) return '';
    return iconValue.replace('custom:', '');
  }

  /**
   * ⭐ NOVO: Recarrega os ícones customizados do MKTPICONS
   */
  reloadCustomIcons(): void {
    this.iconManagerService.reloadCustomIcons();
    // Recarregar a lista de ícones disponíveis
    setTimeout(() => {
      this.loadAvailableTabIcons();
      this.cdr.markForCheck();
      this.message.success('Ícones customizados recarregados!');
    }, 500);
  }

  /**
   * Condição SQL para buscar configurações MKTPCFG disponíveis
   */
  get flowCondition(): string {
    const codecompany = this.uas.user.codecompany;
    return `(CODECOMPANY = 0 AND ONLINE ='S' AND EXISTS (SELECT 1 FROM Mktpcfgcompany cfg WHERE cfg.idconf = idconf and cfg.codecompany = ${codecompany})) OR (CODECOMPANY = ${codecompany} AND ONLINE = 'S')`;
  }

  /**
   * Lista de empresas do usuário logado
   */
  get userCompanies() {
    return this.uas.user.companylist || [];
  }

  /**
   * Abre modal para configurar fluxo
   */
  openFlowConfigModal(): void {
    const currentConfig = this.form.get('flowConfig')?.value || {};
    
    const dialogRef = this.dialog.open(FlowConfigModalComponent, {
      width: '750px',
      maxWidth: '90vw',
      data: {
        idConf: currentConfig.idConf,
        confName: currentConfig.confName,
        showCodeCompany: currentConfig.showCodeCompany,
        codeCompany: currentConfig.codeCompany,
        newMode: currentConfig.newMode
      } as FlowConfigData,
      disableClose: false,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe((result: FlowConfigData) => {
      if (result) {
        // Atualiza o form com os valores do modal
        this.form.patchValue({
          flowConfig: {
            idConf: result.idConf,
            confName: result.confName,
            showCodeCompany: result.showCodeCompany,
            codeCompany: result.codeCompany,
            newMode: result.newMode
          }
        }, { emitEvent: true });

        this.message.success('Configuração de fluxo atualizada');
      }
    });
  }

  /**
   *  NOVO: Retorna lista de panels disponíveis para Master-Detail
   */
  getAvailablePanels(): Array<{ id: string; name: string }> {
    const allNodes = this.builderService.currentState.nodes;
    return allNodes
      .filter(node => node.type === 'panel')
      .map(panel => ({
        id: panel.id,
        name: (panel.props as any)?.name || panel.id
      }));
  }

  /**
   *  NOVO: Move painel para cima na ordem
   */
  movePanelUp(): void {
    if (!this.currentSelectedId) {
      this.message.warning('Nenhum painel selecionado');
      return;
    }
    
    this.builderService.movePanelUp(this.currentSelectedId);
    this.message.success('Painel movido para cima');
  }

  /**
   *  NOVO: Move painel para baixo na ordem
   */
  movePanelDown(): void {
    if (!this.currentSelectedId) {
      this.message.warning('Nenhum painel selecionado');
      return;
    }
    
    this.builderService.movePanelDown(this.currentSelectedId);
    this.message.success('Painel movido para baixo');
  }

  /**
   *  NOVO: Move qualquer componente para cima (genérico)
   */
  moveComponentUp(): void {
    if (!this.currentSelectedId) {
      this.message.warning('Nenhum componente selecionado');
      return;
    }
    
    this.builderService.moveComponentUp(this.currentSelectedId);
    this.message.success('Componente movido para cima');
  }

  /**
   *  NOVO: Move qualquer componente para baixo (genérico)
   */
  moveComponentDown(): void {
    if (!this.currentSelectedId) {
      this.message.warning('Nenhum componente selecionado');
      return;
    }
    
    this.builderService.moveComponentDown(this.currentSelectedId);
    this.message.success('Componente movido para baixo');
  }

  /**
   *  LEGACY: Move Row para cima (usa método genérico)
   */
  moveRowUp(): void {
    this.moveComponentUp();
  }

  /**
   *  LEGACY: Move Row para baixo (usa método genérico)
   */
  moveRowDown(): void {
    this.moveComponentDown();
  }

  // --- Migração de campos entre containers ---

  /**
   * Resolve o panel-pai de um node, subindo pela hierarquia de rows se necessário.
   */
  private resolveParentPanelId(node: BuilderNode): string | null {
    const allNodes = this.builderService.currentState.nodes;
    let currentId = node.parentId ?? null;
    for (let i = 0; i < 20 && currentId; i++) {
      const parent = allNodes.find(n => n.id === currentId);
      if (!parent) return null;
      if (parent.type === 'panel') return parent.id;
      currentId = parent.parentId ?? null;
    }
    return null;
  }

  /**
   * Resolve o label de uma tab pelo tabId, buscando no nó tabs correspondente.
   */
  private resolveTabLabel(tabId: string | null | undefined): string {
    if (!tabId) return '';
    const allNodes = this.builderService.currentState.nodes;
    const tabsNode = allNodes.find(n => n.type === 'tabs');
    if (!tabsNode) return '';
    const items = (tabsNode.props as any)?.items as Array<{ id: string; name: string }> | undefined;
    const tab = items?.find(t => t.id === tabId);
    return tab?.name || '';
  }

  get canImportFields(): boolean {
    return this.selectedComponentType === 'panel' || this.selectedComponentType === 'row';
  }

  get selectedImportCount(): number {
    return this.importGroups.reduce((sum, g) => sum + g.items.filter(it => it.selected).length, 0);
  }

  get totalImportItems(): number {
    return this.importGroups.reduce((sum, g) => sum + g.items.length, 0);
  }

  getTypeIcon(type: string): string {
    const map: Record<string, string> = {
      text: 'font-size', number: 'number', search: 'search', dropdown: 'down-square',
      switch: 'check-square', button: 'play-circle', image: 'picture', textarea: 'align-left',
      tag: 'tags', divider: 'line', row: 'column-width', checkbox: 'check-square'
    };
    return map[type] || 'form';
  }

  getTypeColor(type: string): string {
    const map: Record<string, string> = {
      text: '#1890ff', number: '#722ed1', search: '#13c2c2', dropdown: '#eb2f96',
      switch: '#52c41a', button: '#fa8c16', image: '#f5222d', textarea: '#2f54eb',
      tag: '#a0d911', row: '#faad14'
    };
    return map[type] || '#8c8c8c';
  }

  openImportFieldsModal(): void {
    if (!this.currentSelectedId) return;
    const allNodes = this.builderService.currentState.nodes;
    const targetNode = allNodes.find(n => n.id === this.currentSelectedId);
    if (!targetNode) return;

    const targetIsRow = targetNode.type === 'row';
    const targetId = targetNode.id;

    const descendantIds = new Set<string>();
    const queue = [targetId];
    while (queue.length > 0) {
      const id = queue.shift()!;
      descendantIds.add(id);
      allNodes.filter(n => n.parentId === id).forEach(n => queue.push(n.id));
    }

    const panelMap = new Map<string, typeof this.importGroups[0]>();

    for (const n of allNodes) {
      if (descendantIds.has(n.id)) continue;
      if (n.type === 'tabs' || n.type === 'panel' || n.type === 'datagrid') continue;
      if (targetIsRow && n.type === 'row') continue;

      const parentNode = allNodes.find(p => p.id === n.parentId);
      if (parentNode && parentNode.type === 'row') continue;

      const parentPanelId = this.resolveParentPanelId(n) || '__root__';
      const panel = allNodes.find(p => p.id === parentPanelId);
      const panelName = panel ? ((panel.props as any)?.name || 'Panel') : '(sem painel)';
      const tabLabel = panel ? this.resolveTabLabel(panel.tabId) : '';

      if (!panelMap.has(parentPanelId)) {
        panelMap.set(parentPanelId, { panelId: parentPanelId, panelName, tabLabel, expanded: true, items: [] });
      }

      const label = (n.props as any)?.label || (n.props as any)?.name || n.id.substring(0, 12);
      const isRow = n.type === 'row';
      const rowChildren = isRow
        ? allNodes.filter(c => c.parentId === n.id).map(c => ({
            id: c.id,
            label: (c.props as any)?.label || (c.props as any)?.name || c.id.substring(0, 12),
            type: c.type
          }))
        : undefined;

      panelMap.get(parentPanelId)!.items.push({
        id: n.id, label, type: n.type, selected: false,
        isRow, childCount: rowChildren?.length, children: rowChildren
      });
    }

    for (const group of panelMap.values()) {
      group.items.sort((a, b) => {
        if (a.isRow && !b.isRow) return -1;
        if (!a.isRow && b.isRow) return 1;
        return a.label.localeCompare(b.label);
      });
    }

    this.importGroups = Array.from(panelMap.values())
      .sort((a, b) => (a.panelName + a.tabLabel).localeCompare(b.panelName + b.tabLabel));
    this.importSearchText = '';
    this.showImportFieldsModal = true;
    this.cdr.markForCheck();
  }

  getFilteredGroupItems(group: typeof this.importGroups[0]) {
    if (!this.importSearchText) return group.items;
    const term = this.importSearchText.toLowerCase();
    return group.items.filter(it =>
      it.label.toLowerCase().includes(term) || it.type.toLowerCase().includes(term)
    );
  }

  isGroupAllSelected(group: typeof this.importGroups[0]): boolean {
    const items = this.getFilteredGroupItems(group);
    return items.length > 0 && items.every(it => it.selected);
  }

  isGroupIndeterminate(group: typeof this.importGroups[0]): boolean {
    const items = this.getFilteredGroupItems(group);
    const sel = items.filter(it => it.selected).length;
    return sel > 0 && sel < items.length;
  }

  toggleGroupSelection(group: typeof this.importGroups[0], checked: boolean): void {
    for (const it of this.getFilteredGroupItems(group)) {
      it.selected = checked;
    }
  }

  toggleImportItem(item: typeof this.importGroups[0]['items'][0]): void {
    item.selected = !item.selected;
  }

  selectAllImportItems(checked: boolean): void {
    for (const group of this.importGroups) {
      for (const it of this.getFilteredGroupItems(group)) {
        it.selected = checked;
      }
    }
  }

  confirmImportFields(): void {
    if (!this.currentSelectedId) return;
    const allSelected: typeof this.importGroups[0]['items'] = [];
    for (const g of this.importGroups) {
      allSelected.push(...g.items.filter(it => it.selected));
    }
    if (allSelected.length === 0) {
      this.message.warning('Nenhum item selecionado');
      return;
    }

    const rowIds = new Set(allSelected.filter(it => it.isRow).map(it => it.id));
    const fieldIds = allSelected
      .filter(it => !it.isRow)
      .filter(it => {
        const node = this.builderService.currentState.nodes.find(n => n.id === it.id);
        return !node || !rowIds.has(node.parentId!);
      })
      .map(it => it.id);

    const idsToMove = [...rowIds, ...fieldIds];
    this.builderService.moveNodesToContainer(idsToMove, this.currentSelectedId);

    this.showImportFieldsModal = false;
    this.message.success(`${idsToMove.length} item(ns) importado(s) com sucesso`);
    this.cdr.markForCheck();
  }

  /**
   *  NOVO: Define rapidamente o colSpan usando botões de atalho
   */
  setColSpan(value: number): void {
    this.form.get('colSpan')?.setValue(value);
    this.form.get('colSpan')?.markAsDirty();
    // Força atualização imediata
    if (this.currentSelectedId) {
      this.builderService.updateNodeProps(this.currentSelectedId, { colSpan: value } as any);
    }
  }

  /**
   *  NOVO: Força a recriação completa do componente (similar a editar JSON e salvar)
   */
  forceComponentReload(): void {
    if (!this.currentSelectedId) {
      this.message.warning('Nenhum componente selecionado');
      return;
    }

    // 1. Salva as propriedades atuais do formulário
    const values = this.form.value;
    
    // 2. Monta as propriedades baseado no tipo do componente
    let props: any = {
      label: values.label,
      placeholder: values.placeholder,
      colSpan: values.colSpan,
      rowSpan: values.rowSpan,
      backgroundColor: values.backgroundColor,
      textColor: values.textColor,
      required: values.required || false
    };

    // 3. Adiciona propriedades específicas do tipo
    if (this.currentType === 'search') {
      props.showKey = values.showKey || false;
      props.searchProperties = {
        entityName: values.searchEntityName || 'Mktpproducts',
        fields: {
          keyField: values.searchKeyField || 'CODEPRODUCT',
          valueField: values.searchValueField || 'NAME'
        }
      };
    } else if (this.currentType === 'dropdown') {
      // Coletar opções do FormArray, incluindo isDefault
      const options = (values.dropdownOptions as any[])?.map((opt: any) => ({
        value: opt.value,
        label: opt.label,
        isDefault: opt.isDefault || false
      })) || [];
      props.options = options;
      props.multiple = values.multiple || false;
      props.showSearch = values.showSearch || false;
      // Calcular defaultValue baseado na opção marcada como default
      const defaultOption = options.find((opt: any) => opt.isDefault);
      props.defaultValue = defaultOption ? defaultOption.value : null;
    } else if (this.currentType === 'querySelect') {
      props.condition = values.condition || '';
      props.showKey = values.showKey || false;
      props.entityName = values.entityName || '';
    }

    // 4. Atualiza as propriedades do node
    this.builderService.updateNodeProps(this.currentSelectedId, props);

    // 5. Força recriação: remove temporariamente e adiciona de volta
    const currentState = (this.builderService as any).stateSubject.getValue();
    const targetNode = currentState.nodes.find((n: any) => n.id === this.currentSelectedId);
    
    if (targetNode) {
      // Remove o node temporariamente
      const nodesWithoutTarget = currentState.nodes.filter((n: any) => n.id !== this.currentSelectedId);
      (this.builderService as any).stateSubject.next({
        ...currentState,
        nodes: nodesWithoutTarget
      });

      // Adiciona de volta no próximo tick com updateKey incrementado massivamente
      setTimeout(() => {
        const updatedNode = {
          ...targetNode,
          props: { ...targetNode.props, ...props },
          updateKey: (targetNode.updateKey || 0) + 1000 // Incremento massivo para garantir detecção
        };
        
        (this.builderService as any).stateSubject.next({
          ...currentState,
          nodes: [...nodesWithoutTarget, updatedNode]
        });

        this.message.success('✅ Configurações aplicadas! Componente recriado com sucesso.');
      }, 10);
    }
  }

  /**
   *  NOVO: Retorna mensagem de status da configuração Master-Detail
   */
  getMasterDetailStatusMessage(): string {
    const panelId = this.form.get('masterDetailFormPanel')?.value;
    if (!panelId) {
      return 'Nenhum panel selecionado';
    }
    
    const panel = this.getAvailablePanels().find(p => p.id === panelId);
    if (!panel) {
      return `Panel "${panelId}" não encontrado`;
    }
    
    return `Ao selecionar uma linha da grid, os campos do panel "${panel.name}" serão preenchidos automaticamente.`;
  }

  /**
   *  NOVO: Auto-gera campos de formulário baseado nas colunas do DataGrid
   */
  private autoGenerateFormFields(panelId: string, columns: any[]): void {
    console.log('🤖 Auto-gerando campos para Master-Detail...', { panelId, columns });

    // Verificar se o panel existe
    const panel = this.getAvailablePanels().find(p => p.id === panelId);
    if (!panel) {
      this.message.error(`Panel "${panelId}" não encontrado`);
      return;
    }

    // Verificar se já existem campos no panel
    const existingFields = this.builderService.currentState.nodes.filter(
      node => node.parentId === panelId
    );

    if (existingFields.length > 0) {
      this.message.warning(
        `Panel "${panel.name}" já contém ${existingFields.length} campo(s). Auto-geração cancelada para evitar duplicatas.`,
        { nzDuration: 5000 }
      );
      return;
    }

    // Criar um campo para cada coluna
    let currentRow = 0;
    let fieldsCreated = 0;

    columns.forEach((column, index) => {
      // Skip da coluna 'id' se existir (geralmente não é editável)
      if (column.field === 'id' && index === 0) {
        return;
      }

      // Determinar tipo de componente baseado no tipo da coluna
      const componentType = this.mapColumnTypeToComponentType(column.type);
      
      // Criar o campo
      const fieldId = `${panelId}_field_${column.field}_${Date.now()}_${index}`;
      
      const newField: BuilderNode = {
        id: fieldId,
        type: componentType as any,
        props: {
          label: column.header || this.formatLabel(column.field),
          placeholder: `Digite ${column.header || column.field}...`,
          field: column.field,
          dataPath: column.dataPath || column.field,
          colSpan: 24,
          rowSpan: 1,
          required: false,
          ...this.getDefaultPropsForType(componentType, column)
        } as any,
        parentId: panelId,
        tabId: null
      };

      // Adicionar o campo diretamente ao state
      const currentState = this.builderService.currentState;
      const updatedNodes = [...currentState.nodes, newField];
      
      // Usar reflexão para acessar o stateSubject privado (workaround)
      (this.builderService as any).stateSubject.next({
        nodes: updatedNodes,
        selectedId: currentState.selectedId
      });
      
      fieldsCreated++;
      currentRow++;

      console.log(`✅ Campo criado: ${column.field} (${componentType})`, newField);
    });

    if (fieldsCreated > 0) {
      // Forçar detecção de mudanças no canvas
      this.cdr.markForCheck();
      
      this.message.success(
        `🎉 ${fieldsCreated} campo(s) criado(s) automaticamente no panel "${panel.name}"!`,
        { nzDuration: 5000 }
      );
      
      console.log(`🎉 Total de ${fieldsCreated} campos criados com sucesso!`);
    } else {
      this.message.info('Nenhum campo foi criado (apenas ID foi ignorado)');
    }
  }

  /**
   *  NOVO: Mapeia tipo de coluna do DataGrid para tipo de componente
   */
  private mapColumnTypeToComponentType(columnType: string): string {
    const typeMap: Record<string, string> = {
      'text': 'text',
      'number': 'number',
      'date': 'date',
      'boolean': 'switch',
      'email': 'text',
      'phone': 'text',
      'url': 'text'
    };
    
    return typeMap[columnType] || 'text';
  }

  /**
   *  NOVO: Retorna props padrão baseado no tipo do componente
   */
  private getDefaultPropsForType(componentType: string, column: any): any {
    const defaults: Record<string, any> = {
      'number': {
        min: null,
        max: null,
        step: 1,
        precision: 0
      },
      'date': {
        dateFormat: 'DD/MM/YYYY',
        showTime: false
      },
      'switch': {
        defaultValue: false,
        checkedChildren: 'Sim',
        unCheckedChildren: 'Não'
      },
      'text': {
        maxLength: 255
      }
    };

    return defaults[componentType] || {};
  }

  /**
   *  NOVO: Formata o nome do campo para um label legível
   */
  private formatLabel(fieldName: string): string {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  /**
   *  NOVO: Atualiza preview do valor do dataPath nos dados injetados
   */
  private updateDataPathPreview(dataPath: string): void {
    if (!dataPath) {
      this.dataPathPreview = '';
      this.cdr.markForCheck();
      return;
    }
    
    // Tentar obter valor dos dados injetados
    try {
      const dataBindingService = this.injector.get(DataBindingService, null);
      if (dataBindingService) {
        const currentData = dataBindingService.getCurrentData();
        if (currentData) {
          const value = dataBindingService.resolvePath(currentData, dataPath);
          if (value !== null && value !== undefined) {
            const preview = JSON.stringify(value);
            this.dataPathPreview = preview.length > 50 ? preview.substring(0, 50) + '...' : preview;
          } else {
            this.dataPathPreview = '';
          }
        } else {
          this.dataPathPreview = '';
        }
      } else {
        this.dataPathPreview = '';
      }
    } catch (error) {
      this.dataPathPreview = '';
    }
    this.cdr.markForCheck();
  }

  // ── Helper: Verificar se um node pertence a um painel (percorrendo Rows) ──

  /**
   * Verifica se um node pertence a um dos painéis informados,
   * percorrendo a hierarquia de Rows até encontrar o painel raiz.
   * Ex: campo → Row → Row → Panel  →  retorna true se Panel está em panelIds.
   */
  private isNodeInPanels(node: BuilderNode, panelIds: string[], allNodes: ReadonlyArray<BuilderNode>): boolean {
    const directParent = (node.props as any)?.panel || node.parentId || '';
    if (panelIds.includes(directParent)) return true;

    // Percorrer hierarquia de Rows (máximo 20 níveis para evitar loop infinito)
    let currentId = directParent;
    for (let i = 0; i < 20; i++) {
      const parentNode = allNodes.find(n => n.id === currentId);
      if (!parentNode) break;
      // Se o pai é um Row, continuar subindo
      if (parentNode.type === 'row') {
        const rowParent = parentNode.parentId || '';
        if (panelIds.includes(rowParent)) return true;
        currentId = rowParent;
        continue;
      }
      // Se chegou a um Panel, verificar
      if (parentNode.type === 'panel') {
        return panelIds.includes(parentNode.id);
      }
      break;
    }
    return false;
  }

  // ── Barra de Contexto (Context Strip) ──────────────────────────────

  /**
   * Abre o modal de configuração da Barra de Contexto.
   * Detecta a grade principal, lista campos do painel de formulário,
   * e permite selecionar quais campos aparecem como chips nas abas.
   */
  openContextFieldsModal(): void {
    const nodes = this.builderService.currentState?.nodes || [];
    const formFieldTypes = ['text', 'number', 'switch', 'select', 'date',
                            'dropdown', 'querySelect', 'search', 'textarea'];

    // 1. Encontrar a grade principal (dataPath mais curto)
    const grids = nodes.filter(n => n.type === 'datagrid' && (n.props as any)?.dataPath);
    let available: ContextFieldConfig[] = [];

    if (grids.length > 0) {
      const mainGrid = grids.reduce((shortest, g) => {
        const len = ((g.props as any).dataPath || '').split('.').length;
        const shortLen = ((shortest.props as any).dataPath || '').split('.').length;
        return len < shortLen ? g : shortest;
      });

      // 2. Encontrar painéis do formulário principal
      const mainGridProps = mainGrid.props as any;
      const panelIds: string[] = [];
      if (mainGridProps.masterDetailFormPanels?.length > 0) {
        panelIds.push(...mainGridProps.masterDetailFormPanels);
      }
      if (mainGridProps.masterDetailFormPanel && !panelIds.includes(mainGridProps.masterDetailFormPanel)) {
        panelIds.push(mainGridProps.masterDetailFormPanel);
      }

      // 3. Listar campos de formulário (sem duplicatas por dataPath)
      // ✅ CORRIGIDO: Usar isNodeInPanels para percorrer hierarquia de Rows
      const seen = new Set<string>();
      available = nodes
        .filter(n => formFieldTypes.includes(n.type)
                 && this.isNodeInPanels(n, panelIds, nodes)
                 && (n.props as any)?.dataPath)
        .filter(n => {
          const dp = (n.props as any).dataPath;
          if (seen.has(dp)) return false;
          seen.add(dp);
          return true;
        })
        .map(n => {
          const cfg: ContextFieldConfig = {
            label: (n.props as any).label || (n.props as any).field || n.id,
            dataPath: (n.props as any).dataPath
          };
          // Enriquecer com metadados de pesquisa para campos search e querySelect
          if (n.type === 'search') {
            cfg.sourceType = 'search';
            const sp = (n.props as any).searchProperties;
            if (sp?.entityName && sp?.fields?.keyField && sp?.fields?.valueField) {
              cfg.searchProperties = {
                entityName: sp.entityName,
                fields: { keyField: sp.fields.keyField, valueField: sp.fields.valueField }
              };
            }
          } else if (n.type === 'querySelect') {
            cfg.sourceType = 'querySelect';
            const props = n.props as any;
            if (props.entityName) {
              cfg.searchProperties = {
                entityName: props.entityName,
                fields: { keyField: props.keyField || 'id', valueField: props.valueField || 'name' }
              };
            }
          }
          return cfg;
        });
    }

    // 4. Campos já selecionados no node atual
    //    Mesclar metadados de pesquisa dos campos disponíveis com os já salvos
    const selectedNode = this.currentSelectedId
      ? nodes.find(n => n.id === this.currentSelectedId)
      : null;
    const existingContextFields: ContextFieldConfig[] = (selectedNode?.props as any)?.contextFields || [];
    // Enriquecer campos existentes com metadados de pesquisa (para configs salvas antes deste recurso)
    const availableMap = new Map(available.map(f => [f.dataPath, f]));
    for (const existing of existingContextFields) {
      const avail = availableMap.get(existing.dataPath);
      if (avail && !existing.searchProperties && avail.searchProperties) {
        existing.sourceType = avail.sourceType;
        existing.searchProperties = avail.searchProperties;
      }
    }
    const selectedPaths = new Set(existingContextFields.map(f => f.dataPath));

    // 5. Abrir modal
    const modalRef = this.modal.create({
      nzTitle: null,
      nzContent: ContextFieldsModalComponent,
      nzFooter: null,
      nzWidth: 520,
      nzClassName: 'context-fields-modal-container'
    });

    modalRef.componentInstance!.availableFields = available;
    modalRef.componentInstance!.selectedDataPaths = selectedPaths;

    // 6. Aguardar resultado e salvar
    modalRef.afterClose.subscribe((result: ContextFieldConfig[] | undefined) => {
      if (result !== undefined && this.currentSelectedId) {
        this.builderService.updateNodeProps(this.currentSelectedId, {
          contextFields: result
        } as any);
        this.cdr.markForCheck();
      }
    });
  }

  // ── Dependências entre campos ──────────────────────────────

  /**
   * Abre o modal de configuração de dependências.
   * Lista campos do formulário como possíveis fontes de dependência (qualquer tipo).
   */
  openSearchDependencyModal(): void {
    const nodes = this.builderService.currentState?.nodes || [];
    const depSourceTypes = ['search', 'querySelect', 'text', 'textarea', 'number', 'dropdown', 'select', 'switch', 'date'];

    const grids = nodes.filter(n => n.type === 'datagrid' && (n.props as any)?.dataPath);
    const panelIds: string[] = [];
    if (grids.length > 0) {
      const mainGrid = grids.reduce((shortest, g) => {
        const len = ((g.props as any).dataPath || '').split('.').length;
        const shortLen = ((shortest.props as any).dataPath || '').split('.').length;
        return len < shortLen ? g : shortest;
      });
      const mainGridProps = mainGrid.props as any;
      if (mainGridProps.masterDetailFormPanels?.length > 0) {
        panelIds.push(...mainGridProps.masterDetailFormPanels);
      }
      if (mainGridProps.masterDetailFormPanel && !panelIds.includes(mainGridProps.masterDetailFormPanel)) {
        panelIds.push(mainGridProps.masterDetailFormPanel);
      }
    }

    const currentDataPath = this.currentSelectedId
      ? (nodes.find(n => n.id === this.currentSelectedId)?.props as any)?.dataPath
      : null;

    const seen = new Set<string>();
    const availableFields: DependencyFieldOption[] = nodes
      .filter(n => depSourceTypes.includes(n.type)
               && (panelIds.length === 0 || this.isNodeInPanels(n, panelIds, nodes))
               && (n.props as any)?.dataPath
               && (n.props as any)?.dataPath !== currentDataPath
               && n.id !== this.currentSelectedId)
      .filter(n => {
        const dp = (n.props as any).dataPath;
        if (seen.has(dp)) return false;
        seen.add(dp);
        return true;
      })
      .map(n => {
        const props = n.props as any;
        let keyField = '';
        if (n.type === 'search' && props.searchProperties?.fields?.keyField) {
          keyField = props.searchProperties.fields.keyField;
        } else if (n.type === 'querySelect' && props.keyField) {
          keyField = props.keyField;
        } else {
          keyField = props.dataPath || '';
        }
        return {
          dataPath: props.dataPath,
          label: props.label || props.field || n.id,
          keyField,
          fieldType: n.type
        };
      });

    // Dependências existentes no node atual
    const selectedNode = this.currentSelectedId
      ? nodes.find(n => n.id === this.currentSelectedId)
      : null;
    const existingDeps: SearchFieldDependency[] = (selectedNode?.props as any)?.dependencies || [];

    // Abrir modal
    const modalRef = this.modal.create({
      nzTitle: null,
      nzContent: SearchDependencyModalComponent,
      nzFooter: null,
      nzWidth: 560,
      nzClassName: 'search-dependency-modal-container'
    });

    modalRef.componentInstance!.availableFields = availableFields;
    modalRef.componentInstance!.existingDependencies = existingDeps;

    // Aguardar resultado e salvar
    modalRef.afterClose.subscribe((result: SearchFieldDependency[] | undefined) => {
      if (result !== undefined && this.currentSelectedId) {
        this.builderService.updateNodeProps(this.currentSelectedId, {
          dependencies: result
        } as any);
        this.cdr.markForCheck();
      }
    });
  }

  // ── Campos derivados (Field Mappings) ──────────────────────────────────

  /**
   * Abre o modal de configuração de campos derivados para campos search.
   * Permite mapear colunas da entidade do search para preencher outros campos do formulário.
   */
  openFieldMappingModal(): void {
    const nodes = this.builderService.currentState?.nodes || [];

    // Encontrar o node selecionado
    const selectedNode = this.currentSelectedId
      ? nodes.find(n => n.id === this.currentSelectedId)
      : null;
    if (!selectedNode) return;

    const props = selectedNode.props as any;
    const entityName = props.searchProperties?.entityName || '';

    // Listar todos os campos do formulário como possíveis destinos (exceto o próprio)
    const fieldTypes = ['text', 'textarea', 'number', 'select', 'dropdown', 'search', 'querySelect', 'switch', 'date', 'tag'];
    const currentDataPath = props.dataPath;
    const seen = new Set<string>();

    const availableTargets: FieldMappingTarget[] = nodes
      .filter(n => fieldTypes.includes(n.type)
               && (n.props as any)?.dataPath
               && (n.props as any)?.dataPath !== currentDataPath
               && n.id !== this.currentSelectedId)
      .filter(n => {
        const dp = (n.props as any).dataPath;
        if (seen.has(dp)) return false;
        seen.add(dp);
        return true;
      })
      .map(n => ({
        dataPath: (n.props as any).dataPath,
        label: (n.props as any).label || (n.props as any).field || n.id
      }));

    // Mapeamentos existentes
    const existingMappings: SearchFieldMapping[] = props.fieldMappings || [];

    // Abrir modal
    const modalRef = this.modal.create({
      nzTitle: null,
      nzContent: FieldMappingModalComponent,
      nzFooter: null,
      nzWidth: 600,
      nzClassName: 'field-mapping-modal-container'
    });

    const isSearchField = selectedNode.type === 'search' || selectedNode.type === 'querySelect';
    modalRef.componentInstance!.entityName = entityName;
    modalRef.componentInstance!.availableTargets = availableTargets;
    modalRef.componentInstance!.existingMappings = existingMappings;
    modalRef.componentInstance!.isSearchField = isSearchField;

    // Aguardar resultado e salvar
    modalRef.afterClose.subscribe((result: SearchFieldMapping[] | undefined) => {
      if (result !== undefined && this.currentSelectedId) {
        this.builderService.updateNodeProps(this.currentSelectedId, {
          fieldMappings: result
        } as any);
        this.cdr.markForCheck();
      }
    });
  }
}

